import React, { useEffect, useMemo, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import AssessmentSelect from '../components/AssessmentSelect';
import qldAssessmentQuestions from "../data/qldAssessmentQuestions";

const Green = '#2fb344';
const Grey = '#666666';

const buttonBaseStyles = {
    borderRadius: '8px',
    padding: '12px 20px',
    fontWeight: '700',
    cursor: 'pointer',
    border: '1px solid transparent',
    minWidth: '120px',
};

const secondaryButtonStyles = {
    ...buttonBaseStyles,
    backgroundColor: '#fff',
    color: '#111',
    borderColor: '#d0d0d0',
};

const primaryButtonStyles = {
    ...buttonBaseStyles,
    backgroundColor: '#111',
    color: '#fff',
};

const pdfBlue = '#1670c9';
const pdfBorder = '#b9b9b9';
const pdfHeaderFill = '#e6e6e6';
const pdfAnswerRed = '#ef3b3b';

const partSectionLabelStyles = {
    color: 'black',
    fontWeight: '600',
    fontSize: '17px',
    marginBottom: '10px',
};

const questionPromptTextStyles = {
    color: '#111',
    fontWeight: '600',
    fontSize: '17px',
    lineHeight: 1.45,
};

const textareaStyles = {
    width: '100%',
    minWidth: 0,
    padding: '6px 10px',
    borderRadius: '8px',
    border: '1px solid #ccc',
    fontFamily: 'inherit',
    fontSize: '14px',
    lineHeight: '1.2',
    resize: 'none',
    boxSizing: 'border-box',
    height: 'auto',
    overflow: 'hidden',
};

const markToggleContainerStyles = {
    display: 'inline-flex',
    border: '1px solid #cfd6dd',
    borderRadius: '10px',
    overflow: 'hidden',
    backgroundColor: '#fff',
};

const getMarkToggleButtonStyles = (selected = false, compact = false) => ({
    border: 0,
    backgroundColor: selected ? '#111' : '#fff',
    color: selected ? '#fff' : '#111',
    fontWeight: '700',
    padding: compact ? '8px 12px' : '10px 16px',
    fontSize: compact ? '14px' : '15px',
    marginTop: 0,
    lineHeight: 1,
});

const getAnswerAreaStyles = (highlight = false) => ({
    marginTop: '8px',
    padding: highlight ? '14px' : 0,
    borderRadius: '10px',
    border: highlight ? '2px solid #c62828' : 'none',
    boxSizing: 'border-box',
});

const getQuestionTextareaStyles = (question, maxWidth = null) => ({
    ...textareaStyles,
    ...(maxWidth ? { maxWidth } : {}),
    ...(question?.disableTextareaResize ? { resize: 'none' } : {}),
});

const assessment1PageGroups = [
    [1, 2, 3],
    [4],
    [5, 6],
    [7, 8],
    [9, 10],
    [11, 12, 13],
    [14, 15],
    [16, 17, 18],
    [19, 20, 21],
    [22, 23],
    [24],
    [25],
    [26],
    [27, 28],
    [29, 30],
    [31, 32, 33],
    [34, 35, 36],
    [37, 38],
    [39, 40],
    [41, 42],
    [43, 44],
    [45, 46],
    [47, 48],
    [49, 50],
    [51],
    [52],
];

const assessment2PageGroups = [
    [1],
    [2],
    [3, 4, 5, 6],
    [7],
    [8, 9],
    [10],
    [11],
    [12],
];

const getAssessmentPageGroups = (questions) => {
    if (questions.length === 52) {
        return assessment1PageGroups;
    }

    if (questions.length === 12) {
        return assessment2PageGroups;
    }

    return [questions.map((question) => question.id)];
};

const convertTableRespToArray = (obj) => {
    let idx = 0;
    const arr = [];

    while (Object.prototype.hasOwnProperty.call(obj || {}, idx)) {
        arr.push(obj[idx]);
        idx += 1;
    }

    return arr;
};

const isMultiSelectQuestion = (question) => Array.isArray(question?.answers);
const getRequiredSelectionCount = (question) => (
    isMultiSelectQuestion(question) && question?.type === 'options'
        ? question.answers.length
        : 0
);
const isHybridQuestion = (question) => Boolean(question?.partBQuestion);
const hasManualAnswerSection = (question) => (
    Boolean(question?.textAnswer)
    || isHybridQuestion(question)
    || ['form', 'tableForm', 'imageTableForm', 'textTableForm'].includes(question?.type)
);
const granularImageTableQuestionIds = new Set([7, 51, 52]);
const isGranularImageTableQuestion = (question) => (
    question?.type === 'imageTableForm' && granularImageTableQuestionIds.has(question?.id)
);
const isGranularTableFormQuestion = (question) => (
    question?.type === 'tableForm' && question?.tableForm?.studentHeader?.[0] === 'Hazard Report Form'
);
const usesGranularRetryFeedback = (question) => (
    isGranularImageTableQuestion(question) || isGranularTableFormQuestion(question)
);
const getImageTableCellReviewKey = (rowIndex, columnKey) => `tableCell_${rowIndex}_${columnKey}`;
const getTableFormFieldReviewKey = (rowIndex, fieldKey) => `tableField_${rowIndex}_${fieldKey}`;
const getImageTableRowReviewKeys = (question, rowIndex) => (
    isGranularImageTableQuestion(question)
        ? (question.inputColumns || []).map((column) => getImageTableCellReviewKey(rowIndex, column.key))
        : [getTableRowReviewKey(rowIndex)]
);
const getTableFormRowReviewKeysForRow = (question, row, rowIndex) => {
    if (row?.assessorOnly || row?.type === 'section' || row?.type === 'static') {
        return [];
    }

    if (isGranularTableFormQuestion(question)) {
        if (row?.type === 'inlineFields') {
            return (row.fields || []).map((field) => getTableFormFieldReviewKey(rowIndex, field.key));
        }

        return row?.key ? [getTableFormFieldReviewKey(rowIndex, row.key)] : [];
    }

    return [getTableRowReviewKey(rowIndex)];
};
const getEmbeddedAssessorMarkKeys = (question) => {
    if (question?.type === 'tableForm') {
        return (question.tableForm?.rows || []).reduce((acc, row) => {
            if (row?.assessorField?.inputType === 'select' && row.assessorField.options?.includes('C') && row.assessorField.options?.includes('NYC')) {
                acc.push(row.assessorField.key);
            } else if (row?.assessorOnly && row?.inputType === 'select' && row.options?.includes('C') && row.options?.includes('NYC')) {
                acc.push(row.key);
            }

            return acc;
        }, []);
    }

    if (question?.type === 'imageTableForm' && question?.assessorMarkPerRow) {
        return (question.table?.body || []).map((_, rowIndex) => `assessorMark_${rowIndex}`);
    }

    return [];
};
const usesCnycOptions = (fieldOptions = []) => (
    Array.isArray(fieldOptions)
    && fieldOptions.length === 2
    && fieldOptions.includes('C')
    && fieldOptions.includes('NYC')
);
const getTableFormRowReviewKeys = (question) => {
    if (question?.type !== 'tableForm' || !question?.assessorMarkPerRow) {
        return [];
    }

    return (question.tableForm?.rows || []).reduce((acc, row, rowIndex) => {
        acc.push(...getTableFormRowReviewKeysForRow(question, row, rowIndex));
        return acc;
    }, []);
};
const getInlineRowReviewKeys = (question) => {
    if (question?.type === 'form') {
        return (question.options || []).reduce((acc, row, rowIndex) => {
            if (row?.type === 'section') {
                return acc;
            }

            acc.push(getFormRowReviewKey(rowIndex));
            return acc;
        }, []);
    }

    if (question?.type === 'textTableForm') {
        return (question.table?.body || []).map((_, rowIndex) => getTableRowReviewKey(rowIndex));
    }

    if (question?.type === 'imageTableForm' && question?.assessorMarkPerRow) {
        const tableRowKeys = (question.table?.body || []).flatMap((_, rowIndex) => getImageTableRowReviewKeys(question, rowIndex));
        const partBRowKeys = (question.partBFields || []).map((_, rowIndex) => getPartBRowReviewKey(rowIndex));
        return [...tableRowKeys, ...partBRowKeys];
    }

    return getTableFormRowReviewKeys(question);
};
const hasEmbeddedAssessorControls = (question) => (
    getEmbeddedAssessorMarkKeys(question).length > 0
);
const getFormRowReviewKey = (rowIndex) => `row_${rowIndex}`;
const getTableRowReviewKey = (rowIndex) => `tableRow_${rowIndex}`;
const getPartBRowReviewKey = (rowIndex) => `partBRow_${rowIndex}`;
const getRowReviewMark = (trainerFeedback = {}, reviewKey) => (
    trainerFeedback?.rowMarks?.[reviewKey]?.mark || ''
);
const getRowReviewComment = (trainerFeedback = {}, reviewKey) => (
    trainerFeedback?.rowMarks?.[reviewKey]?.comment || ''
);
const getReviewGroupMark = (trainerFeedback = {}, reviewKeys = []) => {
    const marks = reviewKeys.map((reviewKey) => getRowReviewMark(trainerFeedback, reviewKey)).filter(Boolean);

    if (marks.length === 0) {
        return '';
    }

    if (marks.every((value) => value === 'C')) {
        return 'C';
    }

    if (marks.every((value) => value === 'NYC')) {
        return 'NYC';
    }

    return '';
};
const sectionOnlyRetryQuestionIds = new Set([4, 5, 10, 24, 25, 26]);
const shouldUseSectionOnlyRetryFeedback = (question) => (
    usesGranularRetryFeedback(question)
    || (Boolean(question?.partBQuestion) && sectionOnlyRetryQuestionIds.has(question?.id))
);
const isEligibleForStudentAutoReview = (question) => (
    Boolean(question)
    && !hasManualAnswerSection(question)
    && question?.excludeFromStudentAutoReview !== true
);
const buildGroupedQuestionIndexes = (questionGroups, allowedQuestionIndexes = null) => {
    if (!allowedQuestionIndexes?.length) {
        return questionGroups;
    }

    const allowedIndexSet = new Set(allowedQuestionIndexes);

    return questionGroups
        .map((group) => group.filter((questionIndex) => allowedIndexSet.has(questionIndex)))
        .filter((group) => group.length > 0);
};

const chunkQuestionIndexes = (questionIndexes, pageSize = 2) => {
    const pages = [];
    for (let index = 0; index < questionIndexes.length; index += pageSize) {
        pages.push(questionIndexes.slice(index, index + pageSize));
    }
    return pages;
};
const normalizeSelectionArray = (values = []) => [...values].sort((left, right) => left.localeCompare(right));

const renderHtmlContent = (html) => (
    <span dangerouslySetInnerHTML={{ __html: html }} />
);

const autoResizeTextarea = (element) => {
    if (!element) {
        return;
    }

    const nextElement = element;
    nextElement.style.height = 'auto';
    nextElement.style.height = `${nextElement.scrollHeight}px`;
};

const prefaceSectionLabels = new Set([
    'Context of the Assessment',
    'Instructions for Student',
    'Instructions for Assessor',
    'Use of Model Answers',
    'Criteria for Success',
]);

const prefaceBlueHeadingLabels = new Set([
    'Summary of Unit of Competency',
    'Learning outcomes',
    'Methods of assessment',
    'Resources required',
    'Reasonable Adjustment',
]);

const renderAssessorGuideHtml = (html) => {
    if (!html) {
        return null;
    }

    return <div dangerouslySetInnerHTML={{ __html: html }} />;
};

const renderAssessorGuideTable = (question) => {
    if (!question?.assessorGuideTable) {
        return null;
    }

    const { header = [], headerRows = [], body = [], columnWidths = [] } = question.assessorGuideTable;
    const fallbackFirstColumnWidth = header.length === 2 ? '34%' : '22%';

    return (
        <div>
            {question.assessorGuideIntro && (
                <div style={{ marginTop: 0, marginBottom: '12px' }} dangerouslySetInnerHTML={{ __html: question.assessorGuideIntro }} />
            )}
            <div style={{ width: '100%', maxWidth: '1000px', overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', backgroundColor: '#fff' }}>
                    <thead>
                        {(headerRows.length > 0 ? headerRows : [header.map((content) => ({ content }))]).map((headerRow, rowIndex) => (
                            <tr key={`header-row-${rowIndex}`}>
                                {headerRow.map((cell, index) => (
                                    <th
                                        key={`${cell.content}-${rowIndex}-${index}`}
                                        colSpan={cell.colSpan || 1}
                                        rowSpan={cell.rowSpan || 1}
                                        style={{
                                            textAlign: cell.align || 'left',
                                            backgroundColor: pdfHeaderFill,
                                            padding: '10px',
                                            border: `1px solid ${pdfBorder}`,
                                            color: '#111',
                                            width: cell.width
                                                || (headerRows.length === 0
                                                    ? (
                                                        columnWidths[index]
                                                        || (index === 0
                                                            ? fallbackFirstColumnWidth
                                                            : `${(100 - Number.parseFloat(fallbackFirstColumnWidth)) / Math.max(header.length - 1, 1)}%`)
                                                    )
                                                    : undefined),
                                        }}
                                    >
                                        {cell.content}
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody>
                        {body.map((row, rowIndex) => (
                            <tr key={`${row.image}-${rowIndex}`}>
                                <td style={{ padding: '12px 10px', verticalAlign: 'top', border: `1px solid ${pdfBorder}`, width: columnWidths[0] }}>
                                    {row.image ? (
                                        <img
                                            src={row.image}
                                            alt={row.alt || `Assessor guide row ${rowIndex + 1}`}
                                            style={{ width: '100%', maxWidth: '140px', height: 'auto', aspectRatio: '1 / 1', objectFit: 'contain', display: 'block', margin: '0 auto' }}
                                        />
                                    ) : (
                                        <div style={{ color: '#111', fontWeight: '700', lineHeight: 1.45 }}>
                                            {row.label || ''}
                                        </div>
                                    )}
                                </td>
                                {(row.cells || []).map((cell, cellIndex) => (
                                    <td
                                        key={`${row.image}-cell-${cellIndex}`}
                                        style={{
                                            padding: '12px 10px',
                                            verticalAlign: 'top',
                                            border: `1px solid ${pdfBorder}`,
                                            color: '#111',
                                            lineHeight: 1.45,
                                            width: columnWidths[cellIndex + 1],
                                        }}
                                    >
                                        {typeof cell === 'string'
                                            ? <span dangerouslySetInnerHTML={{ __html: cell }} />
                                            : cell}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const renderAssessorGuideContent = (question) => {
    if (!question) {
        return null;
    }

    return (
        <>
            {question.assessorGuideTable ? renderAssessorGuideTable(question) : null}
            {question.assessorGuideHtml ? (
                <div style={question.assessorGuideTable ? { marginTop: '14px' } : undefined}>
                    {renderAssessorGuideHtml(question.assessorGuideHtml)}
                </div>
            ) : null}
        </>
    );
};

const renderFormattedText = (text, keyPrefix = 'formatted-text') => {
    if (!text) {
        return null;
    }

    return text.split('<b>').map((part, index) => (
        index % 2 === 0
            ? <React.Fragment key={`${keyPrefix}-${index}`}>{part}</React.Fragment>
            : <b key={`${keyPrefix}-${index}`}>{part}</b>
    ));
};

const renderFormattedSubtitle = (subtitle) => {
    const text = subtitle || '';
    const parts = text.split(/(correct|one \(\d+\)|two \(\d+\)|three \(\d+\)|four \(\d+\))/g);

    return parts.map((part, index) => {
        if (/^(correct|one \(\d+\)|two \(\d+\)|three \(\d+\)|four \(\d+\))$/i.test(part)) {
            return <b key={`${part}-${index}`}>{part}</b>;
        }

        return <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>;
    });
};

const getOptionLabel = (index) => `${String.fromCharCode(65 + index)})`;

const isAutoMarkedOptionsSection = (question) => (
    question?.type === 'options'
    && (Boolean(question?.answer) || Array.isArray(question?.answers))
);

const getQuestionReviewSectionOptions = (question) => {
    if (question.partBQuestion) {
        if (isAutoMarkedOptionsSection(question)) {
            return [
                { value: 'partB', label: question.partBLabel || 'Part B' },
            ];
        }

        return [
            { value: 'partA', label: question.partALabel || 'Part A' },
            { value: 'partB', label: question.partBLabel || 'Part B' },
            { value: 'both', label: 'Part A and Part B' },
        ];
    }

    if (question.partBFields?.length) {
        return [
            { value: 'partA', label: 'Main table' },
            { value: 'partB', label: question.partBLabel || 'Part B' },
            { value: 'both', label: 'Main table and Part B' },
        ];
    }

    return [];
};

const getDefaultQuestionReviewSection = (question) => {
    const sectionOptions = getQuestionReviewSectionOptions(question);
    return sectionOptions.length === 1 ? sectionOptions[0].value : 'both';
};

const buildRetrySectionState = (question, trainerFeedback = {}, shouldShowTrainerFeedback = false) => {
    if (usesGranularRetryFeedback(question)) {
        return {
            hasSectionSplit: false,
            highlightPartA: false,
            highlightPartB: false,
        };
    }

    const hasSectionSplit = Boolean(question.partBQuestion || question.partBFields?.length);
    if (!hasSectionSplit) {
        return {
            hasSectionSplit: false,
            highlightPartA: shouldShowTrainerFeedback,
            highlightPartB: false,
        };
    }

    const selectedSection = trainerFeedback.reviewSection || getDefaultQuestionReviewSection(question);
    return {
        hasSectionSplit: true,
        highlightPartA: shouldShowTrainerFeedback && (selectedSection === 'partA' || selectedSection === 'both'),
        highlightPartB: shouldShowTrainerFeedback && (selectedSection === 'partB' || selectedSection === 'both'),
    };
};
const hasIncorrectRowMark = (trainerFeedback = {}) => (
    Object.values(trainerFeedback?.rowMarks || {}).some((row) => row?.mark === 'NYC')
);
const renderRowRetryFeedback = (showFeedback, comment = '', options = {}) => (
    showFeedback && ((!options.hideDefaultMessage && !options.customMessage) || options.customMessage || comment) ? (
        <div style={{ marginTop: '8px', color: '#b42318', fontWeight: '700', fontSize: '14px', lineHeight: 1.4 }}>
            {options.customMessage || (!options.hideDefaultMessage ? 'This answer needs to be updated.' : null)}
            {comment ? <div style={{ marginTop: (options.hideDefaultMessage || options.customMessage) ? '4px' : '4px', fontWeight: '400' }}>Trainer note: {comment}</div> : null}
        </div>
    ) : null
);

const getAssessmentContentConfig = (questionCount, fallbackTitle, retryMode = false) => {
    if (questionCount === 52) {
        return {
            title: 'Assessment Task One (1)',
            subtitle: 'Multiple Choice and short answer questions',
            completionHeading: 'Assessment Task One responses submitted.',
            completionMessage: 'Please wait for your trainer to review your answers.',
            retryHeading: 'Assessment Task One (1)',
            retrySubtitle: 'Multiple Choice and short answer questions',
            nextLabel: retryMode ? 'Submit' : 'Next',
        };
    }

    if (questionCount === 12) {
        return {
            title: 'Assessment Task Two (2)',
            subtitle: 'Oral Questions',
            completionHeading: 'Assessment Task Two responses submitted.',
            completionMessage: 'Please wait for your Trainer to review.',
            retryHeading: 'Assessment Task Two',
            retrySubtitle: 'Oral Questions',
            nextLabel: 'Submit',
        };
    }

    if (questionCount === 1 && fallbackTitle === 'Assessment 3') {
        return {
            title: 'Assessment Task Three (3)',
            subtitle: 'Complete a Simple Form',
            completionHeading: 'Assessment Task Three responses submitted.',
            completionMessage: 'Please wait for your Trainer to review.',
            retryIntroHeading: 'Almost there!',
            retryHeading: 'Assessment Task Three (3)',
            retrySubtitle: 'Complete a Simple Form',
            nextLabel: 'Submit',
        };
    }

    if (questionCount === 1 && fallbackTitle === 'Assessment 4') {
        return {
            title: 'Assessment Task Four (4)',
            subtitle: 'Fitting PPE (Observation)',
            completionHeading: 'Assessment Task Four responses submitted.',
            completionMessage: 'Please wait for your Trainer to review.',
            retryIntroHeading: 'Almost there!',
            retryHeading: 'Assessment Task Four (4)',
            retrySubtitle: 'Fitting PPE (Observation)',
            nextLabel: 'Submit',
        };
    }

    return {
        title: fallbackTitle,
        subtitle: '',
        completionHeading: 'Assessment responses submitted.',
        completionMessage: 'Please wait for your Trainer to review.',
        retryIntroHeading: 'Almost there!',
        retryHeading: fallbackTitle,
        retrySubtitle: '',
        nextLabel: 'Submit',
    };
};

const normalizeResponseForQuestion = (question, value) => {
    if (!question) {
        return value;
    }

    if (question.type === 'form' || question.type === 'tableForm' || question.type === 'table') {
        return value || {};
    }

    if (question.type === 'textTableForm') {
        return value || {};
    }

    if (question.type === 'imageTableForm') {
        if (question.partBFields?.length) {
            return {
                tableAnswers: value?.tableAnswers || {},
                partBAnswers: value?.partBAnswers || {},
            };
        }

        return value || {};
    }

    if (isHybridQuestion(question)) {
        return {
            selectedOptions: value?.selectedOptions || (isMultiSelectQuestion(question) ? [] : ''),
            textAnswer: value?.textAnswer || '',
        };
    }

    if (isMultiSelectQuestion(question)) {
        return Array.isArray(value) ? value : [];
    }

    return value || '';
};

const checkIfAnsweredAllFormQs = (question, response) => (
    question.options.every((row, index) => {
        if (row.type === 'section') {
            return true;
        }

        const answer = response?.[index];
        if (row.type === 'date') {
            return Boolean(answer);
        }

        return Boolean(String(answer || '').trim());
    })
);

const checkIfAnsweredAllTableQs = (question, response) => (
    question.options.every((_, index) => Boolean(response?.[index]))
);

const checkIfAnsweredAllTableFormQs = (question, response, assessorMode) => (
    question.tableForm.rows.every((row) => {
        if (row.assessorOnly && !assessorMode) {
            return true;
        }

        if (row.type === 'static' || row.type === 'section') {
            if (row.assessorField) {
                if (!assessorMode) {
                    return true;
                }

                return row.assessorField.required === false
                    ? true
                    : Boolean(String(response?.[row.assessorField.key] || '').trim());
            }

            return true;
        }

        if (row.type === 'inlineFields') {
            return row.fields.every((field) => Boolean(String(response?.[field.key] || '').trim()));
        }

        if (row.required === false) {
            return true;
        }

        return Boolean(String(response?.[row.key] || '').trim());
    })
);

const checkIfAnsweredAllImageTableQs = (question, response) => {
    const tableAnswers = question.partBFields?.length ? response?.tableAnswers : response;
    const partBAnswers = question.partBFields?.length ? response?.partBAnswers : {};

    const hasAnsweredTable = question.table.body.every((_, rowIndex) => (
        question.inputColumns.every((column) => (
            Boolean(tableAnswers?.[rowIndex]?.[column.key]?.trim())
        ))
    ));

    if (!question.partBFields?.length) {
        return hasAnsweredTable;
    }

    return hasAnsweredTable && question.partBFields.every((_, index) => (
        Boolean(String(partBAnswers?.[index] || '').trim())
    ));
};

const hasAnsweredQuestion = (question, response, assessorMode) => {
    if (!question) {
        return false;
    }

    if (question.type === 'form') {
        return checkIfAnsweredAllFormQs(question, response);
    }

    if (question.type === 'table') {
        return checkIfAnsweredAllTableQs(question, response);
    }

    if (question.type === 'tableForm') {
        return checkIfAnsweredAllTableFormQs(question, response, assessorMode);
    }

    if (question.type === 'imageTableForm') {
        return checkIfAnsweredAllImageTableQs(question, response);
    }

    if (question.type === 'textTableForm') {
        return question.table.body.every((_, rowIndex) => (
            question.inputColumns.every((column) => (
                Boolean(response?.[rowIndex]?.[column.key]?.trim())
            ))
        ));
    }

    if (isHybridQuestion(question)) {
        const requiredSelectionCount = getRequiredSelectionCount(question);
        const hasOptionAnswer = isMultiSelectQuestion(question)
            ? Array.isArray(response?.selectedOptions) && response.selectedOptions.length > 0 && (!requiredSelectionCount || response.selectedOptions.length === requiredSelectionCount)
            : Boolean(response?.selectedOptions);

        return hasOptionAnswer && Boolean(String(response?.textAnswer || '').trim());
    }

    if (isMultiSelectQuestion(question)) {
        const requiredSelectionCount = getRequiredSelectionCount(question);
        return Array.isArray(response) && response.length > 0 && (!requiredSelectionCount || response.length === requiredSelectionCount);
    }

    return Boolean(response);
};

const isQuestionCorrect = (question, response) => {
    if (!question || question.textAnswer) {
        return true;
    }

    const solution = question.answer || question.answers;
    const normalizedResponse = question.partBQuestion ? response?.selectedOptions : response;

    if (Array.isArray(solution)) {
        if (question.type !== 'table') {
            const userResponse = Array.isArray(normalizedResponse) ? normalizedResponse : [];
            return normalizeSelectionArray(solution).toString() === normalizeSelectionArray(userResponse).toString();
        }

        const userResponse = question.type === 'table'
            ? convertTableRespToArray(response || {})
            : normalizedResponse;

        return Array.isArray(userResponse) && solution.toString() === userResponse.toString();
    }

    return normalizedResponse === solution;
};

const getReadOnlyFieldStyles = (minHeight = '44px') => ({
    minHeight,
    width: '100%',
    padding: '10px 12px',
    borderRadius: '8px',
    border: '1px solid #d9d9d9',
    backgroundColor: '#f8f8f8',
    color: '#111',
    boxSizing: 'border-box',
    whiteSpace: 'pre-wrap',
    lineHeight: 1.5,
});

const renderReadOnlyFieldValue = (value, options = {}) => {
    const { minHeight = '44px', fieldType = 'text', fieldOptions = [] } = options;
    const normalizedValue = value instanceof Date
        ? value.toLocaleDateString('en-AU')
        : String(value || '').trim();

    if (fieldType === 'singleCheckboxOptions') {
        return (
            <div style={getReadOnlyFieldStyles(minHeight)}>
                <div style={{ display: 'flex', gap: '18px', flexWrap: 'wrap', alignItems: 'center', minHeight: '22px' }}>
                    {fieldOptions.map((option) => (
                        <label
                            key={option}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '8px',
                                color: '#111',
                                fontWeight: '500',
                            }}
                        >
                            <input
                                type="checkbox"
                                checked={normalizedValue === option}
                                disabled
                                readOnly
                                style={{ width: '18px', height: '18px', margin: 0 }}
                            />
                            <span>{option}</span>
                        </label>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div style={getReadOnlyFieldStyles(minHeight)}>
            {normalizedValue || '\u2014'}
        </div>
    );
};

const renderFieldInput = (fieldType, value, onChange, rows = 4, maxWidth = '1000px', fieldOptions = [], inputOptions = {}) => {
    const { disabled = false, disableResize = false, error = false } = inputOptions;

    if (fieldType === 'singleCheckboxOptions') {
        return (
            <div style={{ display: 'flex', gap: '18px', flexWrap: 'wrap', alignItems: 'center', minHeight: '44px' }}>
                {fieldOptions.map((option) => {
                    const isChecked = value === option;

                    return (
                        <label
                            key={option}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '8px',
                                cursor: disabled ? 'default' : 'pointer',
                                color: '#111',
                                fontWeight: '500',
                            }}
                        >
                            <input
                                type="checkbox"
                                disabled={disabled}
                                checked={isChecked}
                                onChange={() => onChange(isChecked ? '' : option)}
                                style={{ width: '18px', height: '18px', margin: 0 }}
                            />
                            <span>{option}</span>
                        </label>
                    );
                })}
            </div>
        );
    }

    if (fieldType === 'select') {
        if (usesCnycOptions(fieldOptions)) {
            return (
                <div style={markToggleContainerStyles}>
                    {fieldOptions.map((option) => (
                        <button
                            key={option}
                            type='button'
                            disabled={disabled}
                            onClick={() => onChange(option)}
                            style={getMarkToggleButtonStyles(value === option)}
                        >
                            {option}
                        </button>
                    ))}
                </div>
            );
        }

        return (
            <select disabled={disabled} value={value || ''} onChange={({ target }) => onChange(target.value)} style={{ width: '100%', maxWidth, padding: '10px', borderRadius: '8px', border: `1px solid ${error ? '#c62828' : '#ccc'}`, fontFamily: 'inherit', fontSize: '15px', boxSizing: 'border-box', backgroundColor: disabled ? '#f8f8f8' : '#fff', color: '#111' }}>
                <option value="">Select</option>
                {fieldOptions.map((option) => <option key={option} value={option}>{option}</option>)}
            </select>
        );
    }

    if (fieldType === 'text') {
        return <input type="text" disabled={disabled} value={value || ''} onChange={({ target }) => onChange(target.value)} style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: `1px solid ${error ? '#c62828' : '#ccc'}`, boxSizing: 'border-box', backgroundColor: disabled ? '#f8f8f8' : '#fff', color: '#111' }} />;
    }

    if (fieldType === 'date') {
        return <DatePicker selected={value || null} onChange={(nextValue) => onChange(nextValue)} dateFormat="dd/MM/yyyy" disabled={disabled} />;
    }

    return (
        <textarea
            disabled={disabled}
            value={value || ''}
            onChange={({ target }) => {
                autoResizeTextarea(target);
                onChange(target.value);
            }}
            rows={rows}
            style={{ ...textareaStyles, maxWidth, resize: disableResize ? 'none' : textareaStyles.resize, backgroundColor: disabled ? '#f8f8f8' : '#fff', color: '#111', border: `1px solid ${error ? '#c62828' : '#ccc'}` }}
            ref={autoResizeTextarea}
        />
    );
};

const QldAssessmentFlow = ({
    embedded = false,
    questionsData = qldAssessmentQuestions,
    title = 'Assessment Form QLD',
    startIndex = 0,
    assessorMode = false,
    initialResponseDocument = null,
    onResponseDocumentChange,
    onAssessmentComplete,
    collectStudentInfoOnComplete = true,
    allowReviewCompleted = false,
    showMarkAllAsC = true,
    assessorReviewData = null,
    onAssessorReviewChange,
    retryQuestionIndexes = [],
    reviewFeedbackData = {},
    prefaceScreens = [],
    studentName = '',
    visibleQuestionIndexes = [],
    studentQuestionOnly = false,
    inlineAssessorGuideMode = false,
    readOnlyStudentResponses = false,
    hideQuestionNumbers = false,
    inlineMarkingMode = false,
    rightAlignedMarkingMode = false,
    embeddedMarkAllCMode = false,
    emphasizeQuestionBorder = false,
    documentVariant = 'default',
    showPrefaceInAssessorMode = false,
}) => {
    const [questions, setQuestions] = useState([]);
    const [responseDocumentData, setResponseDocumentData] = useState({ responses: {}, finalQIndex: 0 });
    const [localAssessorReview, setLocalAssessorReview] = useState(assessorReviewData || { questionMarks: {} });
    const [completedAssessment, setHasCompletedAssessment] = useState(false);
    const [answeringIncorrectQs, setAnsweringIncorrectQs] = useState(false);
    const [currentPageIndex, setCurrentPageIndex] = useState(0);
    const [pageValidationMessage, setPageValidationMessage] = useState('');
    const [showingGuide, setShowingGuide] = useState({});
    const [showingAssessorGuide, setShowingAssessorGuide] = useState({});
    const [unansweredQuestionIds, setUnansweredQuestionIds] = useState([]);
    const [trainerViewMode, setTrainerViewMode] = useState('all');
    const [prefaceScreenIndex, setPrefaceScreenIndex] = useState(0);
    const [showIncorrectReviewScreen, setShowIncorrectReviewScreen] = useState(false);
    const [pendingIncorrectQuestionIndexes, setPendingIncorrectQuestionIndexes] = useState([]);
    const [showRetryIntroScreen, setShowRetryIntroScreen] = useState(false);
    const hasInitializedPageRef = useRef(false);
    const previousPageIndexRef = useRef(0);
    const studentRetryMode = !assessorMode && retryQuestionIndexes.length > 0;
    const isAssessmentThree = questionsData?.length === 1 && title === 'Assessment 3';
    const isAssessmentFour = questionsData?.length === 1 && title === 'Assessment 4';
    const activePrefaceScreens = useMemo(() => (
        (!assessorMode || showPrefaceInAssessorMode) && !studentRetryMode && (prefaceScreens || []).length > 0 ? prefaceScreens : []
    ), [assessorMode, prefaceScreens, showPrefaceInAssessorMode, studentRetryMode]);
    const assessmentContentConfig = useMemo(
        () => getAssessmentContentConfig((questionsData || []).length, title, studentRetryMode),
        [questionsData, studentRetryMode, title]
    );
    const usePdfTheme = documentVariant !== 'default';

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    useEffect(() => {
        setQuestions(questionsData || []);
    }, [questionsData]);

    useEffect(() => {
        setLocalAssessorReview(assessorReviewData || { questionMarks: {} });
    }, [assessorReviewData]);

    useEffect(() => {
        if (!initialResponseDocument) {
            setResponseDocumentData({ responses: {}, finalQIndex: 0 });
            setAnsweringIncorrectQs(false);
            setHasCompletedAssessment(false);
            setShowIncorrectReviewScreen(false);
            setPendingIncorrectQuestionIndexes([]);
            setShowRetryIntroScreen(false);
            hasInitializedPageRef.current = false;
            return;
        }

        if (initialResponseDocument.hasCompletedAssessment && !allowReviewCompleted && !studentRetryMode) {
            setHasCompletedAssessment(true);
            return;
        }

        setResponseDocumentData(initialResponseDocument);
        setAnsweringIncorrectQs(Boolean(initialResponseDocument.answeringIncorrect));
        setHasCompletedAssessment(Boolean(initialResponseDocument.hasCompletedAssessment) && !studentRetryMode);
    }, [allowReviewCompleted, initialResponseDocument, studentRetryMode]);

    useEffect(() => {
        setShowRetryIntroScreen(studentRetryMode && isAssessmentThree);
    }, [isAssessmentThree, studentRetryMode]);

    const pageGroups = useMemo(() => getAssessmentPageGroups(questions), [questions]);

    const questionIndexById = useMemo(() => {
        const map = {};
        questions.forEach((question, index) => {
            map[question.id] = index;
        });
        return map;
    }, [questions]);

    const allGroupedQuestionIndexes = useMemo(() => (
        pageGroups.map((group) => group
            .map((id) => questionIndexById[id])
            .filter((index) => typeof index === 'number'))
            .filter((group) => group.length > 0)
    ), [pageGroups, questionIndexById]);

    const trainerIncorrectQuestionIndexes = useMemo(() => (
        questions.reduce((acc, question, index) => {
            if (
                hasManualAnswerSection(question)
                && (
                    localAssessorReview?.questionMarks?.[index]?.mark === 'NYC'
                    || hasIncorrectRowMark(localAssessorReview?.questionMarks?.[index])
                )
            ) {
                acc.push(index);
            }

            return acc;
        }, [])
    ), [localAssessorReview, questions]);

    const groupedQuestionIndexes = useMemo(() => {
        const displayGroups = assessorMode
            ? allGroupedQuestionIndexes
            : chunkQuestionIndexes(allGroupedQuestionIndexes.flat(), 2);
        const baseGroupedIndexes = visibleQuestionIndexes.length > 0
            ? buildGroupedQuestionIndexes(displayGroups, visibleQuestionIndexes)
            : displayGroups;

        if (studentRetryMode) {
            return buildGroupedQuestionIndexes(baseGroupedIndexes, retryQuestionIndexes);
        }

        if (!assessorMode && answeringIncorrectQs && pendingIncorrectQuestionIndexes.length > 0) {
            return buildGroupedQuestionIndexes(baseGroupedIndexes, pendingIncorrectQuestionIndexes);
        }

        if (assessorMode && trainerViewMode === 'incorrect' && trainerIncorrectQuestionIndexes.length > 0) {
            return buildGroupedQuestionIndexes(baseGroupedIndexes, trainerIncorrectQuestionIndexes);
        }

        return baseGroupedIndexes;
    }, [
        allGroupedQuestionIndexes,
        answeringIncorrectQs,
        assessorMode,
        pendingIncorrectQuestionIndexes,
        retryQuestionIndexes,
        studentRetryMode,
        trainerIncorrectQuestionIndexes,
        trainerViewMode,
        visibleQuestionIndexes,
    ]);

    const activeQuestionIndexes = useMemo(() => groupedQuestionIndexes.flat(), [groupedQuestionIndexes]);

    useEffect(() => {
        if (groupedQuestionIndexes.length === 0) {
            return;
        }

        if (hasInitializedPageRef.current) {
            return;
        }

        const initialQuestionIndex = studentRetryMode
            ? retryQuestionIndexes[0]
            : assessorMode
                ? startIndex
                : initialResponseDocument?.finalQIndex ?? startIndex;
        const initialGroupIndex = groupedQuestionIndexes.findIndex((group) => group.includes(initialQuestionIndex));
        setCurrentPageIndex(initialGroupIndex >= 0 ? initialGroupIndex : 0);
        hasInitializedPageRef.current = true;
    }, [assessorMode, groupedQuestionIndexes, initialResponseDocument, retryQuestionIndexes, startIndex, studentRetryMode]);

    useEffect(() => {
        if (groupedQuestionIndexes.length === 0) {
            return;
        }

        if (currentPageIndex >= groupedQuestionIndexes.length) {
            setCurrentPageIndex(groupedQuestionIndexes.length - 1);
        }
    }, [currentPageIndex, groupedQuestionIndexes]);

    useEffect(() => {
        if (!hasInitializedPageRef.current) {
            previousPageIndexRef.current = currentPageIndex;
            return;
        }

        if (previousPageIndexRef.current !== currentPageIndex) {
            scrollToTop();
            previousPageIndexRef.current = currentPageIndex;
        }
    }, [currentPageIndex]);

    const currentQuestionIndexes = groupedQuestionIndexes[currentPageIndex] || [];
    const currentQuestions = currentQuestionIndexes.map((index) => questions[index]).filter(Boolean);
    const isLastPage = currentPageIndex >= groupedQuestionIndexes.length - 1;

    const getResponseForQuestion = (questionIndex) => {
        const question = questions[questionIndex];
        return normalizeResponseForQuestion(question, responseDocumentData.responses?.[questionIndex]);
    };

    const saveResponse = (questionIndex, nextValue, nextFinalQIndex = questionIndex) => {
        const nextDocData = {
            ...responseDocumentData,
            responses: {
                ...(responseDocumentData.responses || {}),
                [questionIndex]: nextValue,
            },
            finalQIndex: Math.max(responseDocumentData.finalQIndex || 0, nextFinalQIndex),
        };

        setResponseDocumentData(nextDocData);
        onResponseDocumentChange && onResponseDocumentChange(nextDocData);
    };

    const updateQuestionResponse = (questionIndex, nextValue) => {
        saveResponse(questionIndex, nextValue, questionIndex);
        setPageValidationMessage('');
        const questionId = questions[questionIndex]?.id;
        if (questionId) {
            setUnansweredQuestionIds((previous) => previous.filter((id) => id !== questionId));
        }
    };

    const updateAssessorReview = (questionIndex, key, value) => {
        const nextReview = {
            ...(localAssessorReview || { questionMarks: {} }),
            questionMarks: {
                ...((localAssessorReview && localAssessorReview.questionMarks) || {}),
                [questionIndex]: {
                    ...(((localAssessorReview && localAssessorReview.questionMarks) || {})[questionIndex] || {}),
                    ...(key === 'mark' && value === 'C' ? { comment: '' } : {}),
                    [key]: value,
                }
            }
        };

        setLocalAssessorReview(nextReview);
        onAssessorReviewChange && onAssessorReviewChange(nextReview);
    };
    const updateAssessorRowReview = (questionIndex, reviewKey, key, value) => {
        const currentQuestionReview = (((localAssessorReview && localAssessorReview.questionMarks) || {})[questionIndex] || {});
        const currentRowReview = (currentQuestionReview.rowMarks || {})[reviewKey] || {};
        const nextReview = {
            ...(localAssessorReview || { questionMarks: {} }),
            questionMarks: {
                ...((localAssessorReview && localAssessorReview.questionMarks) || {}),
                [questionIndex]: {
                    ...currentQuestionReview,
                    rowMarks: {
                        ...(currentQuestionReview.rowMarks || {}),
                        [reviewKey]: {
                            ...currentRowReview,
                            ...(key === 'mark' && value === 'C' ? { comment: '' } : {}),
                            [key]: value,
                        },
                    },
                }
            }
        };

        setLocalAssessorReview(nextReview);
        onAssessorReviewChange && onAssessorReviewChange(nextReview);
    };
    const updateAssessorRowReviews = (questionIndex, reviewKeys = [], value) => {
        const currentQuestionReview = (((localAssessorReview && localAssessorReview.questionMarks) || {})[questionIndex] || {});
        const nextReview = {
            ...(localAssessorReview || { questionMarks: {} }),
            questionMarks: {
                ...((localAssessorReview && localAssessorReview.questionMarks) || {}),
                [questionIndex]: {
                    ...currentQuestionReview,
                    rowMarks: reviewKeys.reduce((acc, reviewKey) => ({
                        ...acc,
                        [reviewKey]: {
                            ...((currentQuestionReview.rowMarks || {})[reviewKey] || {}),
                            ...(value === 'C' ? { comment: '' } : {}),
                            mark: value,
                        },
                    }), { ...(currentQuestionReview.rowMarks || {}) }),
                }
            }
        };

        setLocalAssessorReview(nextReview);
        onAssessorReviewChange && onAssessorReviewChange(nextReview);
    };

    const markAllQuestionsCompetent = (questionIndexesOrEvent = currentQuestionIndexes) => {
        const questionIndexes = (Array.isArray(questionIndexesOrEvent)
            ? questionIndexesOrEvent
            : currentQuestionIndexes)
            .filter((index) => {
                const question = questions[index];
                if (!question) {
                    return false;
                }

                return hasManualAnswerSection(question) || getEmbeddedAssessorMarkKeys(question).length > 0;
            });

        const nextReview = {
            ...(localAssessorReview || { questionMarks: {} }),
            questionMarks: questionIndexes.reduce((acc, index) => {
                const question = questions[index];
                const existingQuestionReview = (((localAssessorReview && localAssessorReview.questionMarks) || {})[index] || {});
                const existingRowMarks = existingQuestionReview.rowMarks || {};
                const reviewKeys = getInlineRowReviewKeys(question);
                const normalizedRowMarks = reviewKeys.length > 0
                    ? Object.fromEntries(reviewKeys.map((key) => [
                        key,
                        {
                            ...(existingRowMarks[key] || {}),
                            mark: 'C',
                            comment: '',
                        }
                    ]))
                    : Object.fromEntries(
                        Object.entries(existingRowMarks).map(([key, value]) => [key, { ...value, mark: 'C', comment: '' }])
                    );

                return {
                    ...acc,
                    [index]: {
                        ...existingQuestionReview,
                        mark: reviewKeys.length > 0 ? '' : 'C',
                        rowMarks: normalizedRowMarks,
                    }
                };
            }, { ...((localAssessorReview && localAssessorReview.questionMarks) || {}) })
        };

        const nextResponses = questionIndexes.reduce((acc, index) => {
            const question = questions[index];
            const embeddedMarkKeys = getEmbeddedAssessorMarkKeys(question);
            if (embeddedMarkKeys.length === 0) {
                return acc;
            }

            acc[index] = embeddedMarkKeys.reduce((responseAcc, key) => ({
                ...responseAcc,
                [key]: 'C',
            }), {
                ...(responseDocumentData.responses?.[index] || {}),
                assessorComments: '',
            });
            return acc;
        }, { ...(responseDocumentData.responses || {}) });

        const nextDocData = {
            ...responseDocumentData,
            responses: nextResponses,
        };

        setResponseDocumentData(nextDocData);
        onResponseDocumentChange && onResponseDocumentChange(nextDocData);
        setLocalAssessorReview(nextReview);
        onAssessorReviewChange && onAssessorReviewChange(nextReview);
    };
    const renderAssessorRowMarkControl = (questionIndex, reviewKeyOrKeys, compact = false, containerStyle = {}) => {
        const reviewKeys = Array.isArray(reviewKeyOrKeys) ? reviewKeyOrKeys : [reviewKeyOrKeys];
        const selectedMark = reviewKeys.length > 1
            ? getReviewGroupMark(localAssessorReview?.questionMarks?.[questionIndex], reviewKeys)
            : getRowReviewMark(localAssessorReview?.questionMarks?.[questionIndex], reviewKeys[0]);

        return (
            <div style={{ width: compact ? '92px' : '120px', ...containerStyle }}>
                <div style={markToggleContainerStyles}>
                    {['C', 'NYC'].map((option) => (
                        <button
                            key={option}
                            type='button'
                            onClick={() => (
                                reviewKeys.length > 1
                                    ? updateAssessorRowReviews(questionIndex, reviewKeys, option)
                                    : updateAssessorRowReview(questionIndex, reviewKeys[0], 'mark', option)
                            )}
                            style={getMarkToggleButtonStyles(selectedMark === option, compact)}
                        >
                            {option}
                        </button>
                    ))}
                </div>
            </div>
        );
    };

    const getIncorrectQuestionIndexes = (options = {}) => (
        activeQuestionIndexes.reduce((acc, index) => {
            const question = questions[index];
            const response = getResponseForQuestion(index);
            if (options.autoReviewOnly && !isEligibleForStudentAutoReview(question)) {
                return acc;
            }
            if (!isQuestionCorrect(question, response)) {
                acc.push(index);
            }
            return acc;
        }, [])
    );

    const completeAssessment = () => {
        const nextDocData = {
            ...responseDocumentData,
            hasCompletedAssessment: true,
            answeringIncorrect: false,
        };

        setShowIncorrectReviewScreen(false);
        setPendingIncorrectQuestionIndexes([]);
        setResponseDocumentData(nextDocData);
        setHasCompletedAssessment(true);
        onResponseDocumentChange && onResponseDocumentChange(nextDocData);
        onAssessmentComplete && onAssessmentComplete(nextDocData, { assessorReview: localAssessorReview });
    };

    const showIncorrectReviewPrompt = (incorrectQuestionIndexes) => {
        if (incorrectQuestionIndexes.length === 0) {
            completeAssessment();
            return;
        }

        setPendingIncorrectQuestionIndexes(incorrectQuestionIndexes);
        setShowIncorrectReviewScreen(true);
        setUnansweredQuestionIds([]);
        setPageValidationMessage('');

        const nextDocData = {
            ...responseDocumentData,
            answeringIncorrect: true,
        };
        setResponseDocumentData(nextDocData);
        onResponseDocumentChange && onResponseDocumentChange(nextDocData);
    };

    const startIncorrectReview = (incorrectQuestionIndexes = pendingIncorrectQuestionIndexes) => {
        if (incorrectQuestionIndexes.length === 0) {
            completeAssessment();
            return;
        }

        const nextIncorrectPageIndex = groupedQuestionIndexes.findIndex((group) => (
            group.some((questionIndex) => incorrectQuestionIndexes.includes(questionIndex))
        ));

        setShowIncorrectReviewScreen(false);
        setAnsweringIncorrectQs(true);
        setCurrentPageIndex(nextIncorrectPageIndex >= 0 ? nextIncorrectPageIndex : 0);
        setPendingIncorrectQuestionIndexes(incorrectQuestionIndexes);

        const nextDocData = {
            ...responseDocumentData,
            answeringIncorrect: true,
        };
        setResponseDocumentData(nextDocData);
        onResponseDocumentChange && onResponseDocumentChange(nextDocData);
    };

    const goToIncorrectPageIfNeeded = () => {
        showIncorrectReviewPrompt(getIncorrectQuestionIndexes({ autoReviewOnly: true }));
    };

    const validateQuestionIndexes = (questionIndexes) => {
        if (assessorMode) {
            setUnansweredQuestionIds([]);
            setPageValidationMessage('');
            return true;
        }

        if (studentQuestionOnly) {
            setUnansweredQuestionIds([]);
            setPageValidationMessage('');
            return true;
        }

        const unansweredQuestionNumbers = questionIndexes.reduce((acc, questionIndex) => {
            const question = questions[questionIndex];
            const response = getResponseForQuestion(questionIndex);

            if (!hasAnsweredQuestion(question, response, assessorMode)) {
                acc.push(question.id);
            }

            return acc;
        }, []);

        if (unansweredQuestionNumbers.length > 0) {
            setUnansweredQuestionIds(unansweredQuestionNumbers);
            setPageValidationMessage(
                (isAssessmentThree || isAssessmentFour)
                    ? 'Please answer the question in full to proceed.'
                    : `Please answer question${unansweredQuestionNumbers.length > 1 ? 's' : ''} ${unansweredQuestionNumbers.join(', ')} to proceed.`
            );
            return false;
        }

        setUnansweredQuestionIds([]);
        setPageValidationMessage('');
        return true;
    };

    const validateCurrentPage = () => validateQuestionIndexes(currentQuestionIndexes);

    const handleNext = () => {
        if (!validateCurrentPage()) {
            return;
        }

        if (answeringIncorrectQs) {
            const incorrectQuestionIndexes = getIncorrectQuestionIndexes();

            if (incorrectQuestionIndexes.length === 0) {
                completeAssessment();
                return;
            }

            if (isLastPage) {
                showIncorrectReviewPrompt(incorrectQuestionIndexes);
                return;
            }

            const nextIncorrectPageIndex = groupedQuestionIndexes.findIndex((group, groupIndex) => (
                groupIndex > currentPageIndex && group.some((questionIndex) => incorrectQuestionIndexes.includes(questionIndex))
            ));

            if (nextIncorrectPageIndex === -1) {
                showIncorrectReviewPrompt(incorrectQuestionIndexes);
                return;
            }

            setCurrentPageIndex(nextIncorrectPageIndex);
            return;
        }

        if (!isLastPage) {
            setUnansweredQuestionIds([]);
            setCurrentPageIndex((previous) => previous + 1);
            return;
        }

        if (!assessorMode && !validateQuestionIndexes(activeQuestionIndexes)) {
            return;
        }

        if (!studentRetryMode && activeQuestionIndexes.some((questionIndex) => !hasManualAnswerSection(questions[questionIndex])) && !assessorMode) {
            goToIncorrectPageIfNeeded();
            return;
        }

        completeAssessment();
    };

    const handlePrev = () => {
        if (currentPageIndex === 0) {
            return;
        }

        setPageValidationMessage('');
        setUnansweredQuestionIds([]);
        setCurrentPageIndex((previous) => previous - 1);
    };

    const renderAssessorGuide = (question) => {
        if (!assessorMode || inlineAssessorGuideMode || (!question?.assessorGuideHtml && !question?.assessorGuideTable && !question?.assessorGuideResponse)) {
            return null;
        }

        return (
            <div style={{ marginTop: '18px', maxWidth: '1000px' }}>
                <button
                    type='button'
                    onClick={() => setShowingAssessorGuide((previous) => ({ ...previous, [question.id]: !previous[question.id] }))}
                    style={{
                        width: '100%',
                        textAlign: 'left',
                        border: `1px solid ${pdfBorder}`,
                        backgroundColor: '#f8f8f8',
                        color: '#111',
                        padding: '12px 14px',
                        fontWeight: '700',
                        cursor: 'pointer',
                    }}
                >
                    {showingAssessorGuide[question.id] ? 'Hide Assessor Guide' : 'Show Assessor Guide'}
                </button>
                {showingAssessorGuide[question.id] && (
                    <div style={{ padding: '14px', border: `1px solid ${pdfBorder}`, borderTop: 0, color: '#111', lineHeight: 1.45, fontSize: '14px' }}>
                        {renderAssessorGuideContent(question)}
                    </div>
                )}
            </div>
        );
    };

    const renderStudentPromptOnly = (question) => {
        if (question.type === 'form') {
            return (
                <div>
                    {question.img && <div style={{ marginBottom: '20px' }}>
                        <img
                            src={question.img}
                            alt={question.imgAlt || `Question ${question.id}`}
                            style={{
                                width: question.imgWidth || '100%',
                                maxWidth: question.imgMaxWidth || '1000px',
                                height: 'auto',
                                display: 'block'
                            }}
                        />
                    </div>}
                    {question.options.map((row, index) => {
                        if (row.type === 'section') {
                            return (
                                <div key={`${row.name}-${index}`} style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap', color: 'black', fontWeight: '600', marginTop: index === 0 ? 0 : '18px', marginBottom: '10px' }}>
                                    <p style={{ margin: 0 }}>{renderFormattedText(row.name, `student-prompt-section-${question.id}-${index}`)}</p>
                                    {row.inlineImage && (
                                        <img
                                            src={row.inlineImage}
                                            alt={row.inlineImageAlt || `Question ${question.id} prompt`}
                                            style={{ width: row.inlineImageWidth || '140px', maxWidth: '100%', height: 'auto', display: 'block', flex: '0 0 auto' }}
                                        />
                                    )}
                                </div>
                            );
                        }

                        if (row.type === 'text') {
                            return (
                                <div key={`${row.name}-${index}`} style={{ display: 'grid', gridTemplateColumns: 'minmax(140px, 220px) minmax(0, 1fr)', columnGap: '14px', alignItems: 'center', marginBottom: '14px' }}>
                                    <p style={{ margin: 0 }}>{renderFormattedText(row.name, `student-prompt-row-${question.id}-${index}`)}</p>
                                    <div style={getReadOnlyFieldStyles('44px')} />
                                </div>
                            );
                        }

                        return (
                            <div key={`${row.name}-${index}`} style={{ marginBottom: '14px' }}>
                                {(row.name || row.inlineImage) && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap', marginBottom: '10px' }}>
                                        {row.name && <p style={{ margin: 0 }}>{renderFormattedText(row.name, `student-prompt-row-${question.id}-${index}`)}</p>}
                                        {row.inlineImage && (
                                            <img
                                                src={row.inlineImage}
                                                alt={row.inlineImageAlt || `Question ${question.id} prompt`}
                                                style={{ width: row.inlineImageWidth || '140px', maxWidth: '100%', height: 'auto', display: 'block', flex: '0 0 auto' }}
                                            />
                                        )}
                                    </div>
                                )}
                                <div style={getReadOnlyFieldStyles(row.type === 'textarea' ? `${Math.max((row.rows || 4) * 24, 44)}px` : '44px')} />
                            </div>
                        );
                    })}
                </div>
            );
        }

        if (question.type === 'imageTableForm') {
            return (
                <div style={{ width: '100%', maxWidth: '1000px', overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                        <thead>
                            <tr>
                                {question.table.header.map((content, index) => <th key={`${content}-${index}`} style={{ textAlign: question.tableHeaderTextAlign || 'left', backgroundColor: question.tableHeaderBackgroundColor || '#f1f1f1', padding: '10px', borderBottom: '1px solid #d9d9d9', width: index === 0 ? '22%' : `${78 / (question.table.header.length - 1)}%` }}>
                                    {content}
                                </th>)}
                            </tr>
                        </thead>
                        <tbody>
                            {question.table.body.map((row, rowIndex) => <tr key={`${row.image}-${rowIndex}`}>
                                <td style={{ padding: '12px 10px', verticalAlign: 'top', borderBottom: '1px solid #ececec' }}>
                                    <img src={row.image} alt={row.alt || `Question ${question.id} row ${rowIndex + 1}`} style={{ width: '100%', maxWidth: '140px', height: 'auto', aspectRatio: '1 / 1', objectFit: 'contain', display: 'block' }} />
                                </td>
                                {question.inputColumns.map((column, columnIndex) => <td key={`${column.key}-${columnIndex}`} style={{ padding: '12px 10px', verticalAlign: 'top', borderBottom: '1px solid #ececec' }}>
                                    <div style={{ minHeight: `${Math.max((column.rows || 4) * 24, 44)}px`, border: '1px solid #d9d9d9', borderRadius: '8px', backgroundColor: '#f8f8f8' }} />
                                </td>)}
                            </tr>)}
                        </tbody>
                    </table>
                    {question.partBFields?.length > 0 && <div style={{ marginTop: '20px' }}>
                        <p style={partSectionLabelStyles}>{renderFormattedText(question.partBLabel || 'Part B', `student-prompt-partb-${question.id}`)}</p>
                        {question.partBFields.map((field, index) => <p key={`${field.name}-${index}`} style={{ marginBottom: '14px' }}>
                            {renderFormattedText(field.name, `student-prompt-partb-field-${question.id}-${index}`)}
                        </p>)}
                    </div>}
                </div>
            );
        }

        if (question.type === 'textTableForm') {
            return (
                <div style={{ width: '100%', maxWidth: '1000px', overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                        <thead>
                            <tr>
                                {question.table.header.map((content, index) => <th key={content} style={{ textAlign: 'left', padding: '10px', borderBottom: '1px solid #d9d9d9', width: question.stackedInputColumns ? (index === 0 ? '34%' : '66%') : (index === 0 ? '34%' : `${66 / (question.table.header.length - 1)}%`) }}>
                                    {content}
                                </th>)}
                            </tr>
                        </thead>
                        <tbody>
                            {question.table.body.map((row, rowIndex) => <tr key={row.label}>
                                <td style={{ padding: '12px 10px', verticalAlign: 'top', borderBottom: '1px solid #ececec' }}>
                                    {renderFormattedText(row.label, `student-prompt-text-table-${question.id}-${rowIndex}`)}
                                </td>
                                {question.stackedInputColumns ? (
                                    <td style={{ padding: '12px 10px', verticalAlign: 'top', borderBottom: '1px solid #ececec' }}>
                                        {question.inputColumns.map((column, columnIndex) => (
                                            <div key={column.key} style={{ display: 'grid', gridTemplateColumns: '22px minmax(0, 1fr)', columnGap: '8px', alignItems: 'start', marginBottom: columnIndex === question.inputColumns.length - 1 ? 0 : '12px' }}>
                                                <div style={{ paddingTop: '10px', fontWeight: '600' }}>{columnIndex + 1}.</div>
                                                <div style={getReadOnlyFieldStyles(`${Math.max((column.rows || 4) * 24, 44)}px`)} />
                                            </div>
                                        ))}
                                    </td>
                                ) : question.inputColumns.map((column) => <td key={column.key} style={{ padding: '12px 10px', verticalAlign: 'top', borderBottom: '1px solid #ececec' }}>
                                    <div style={getReadOnlyFieldStyles(`${Math.max((column.rows || 4) * 24, 44)}px`)} />
                                </td>)}
                            </tr>)}
                        </tbody>
                    </table>
                </div>
            );
        }

        return null;
    };

    const toggleOptionValue = (question, questionIndex, response, opt) => {
        if (isHybridQuestion(question)) {
            const selectedAnswers = Array.isArray(response?.selectedOptions) ? response.selectedOptions : [];

        if (!isMultiSelectQuestion(question)) {
            updateQuestionResponse(questionIndex, {
                selectedOptions: opt,
                textAnswer: response?.textAnswer || '',
            });
            return;
        }

        const requiredSelectionCount = getRequiredSelectionCount(question);
        if (!selectedAnswers.includes(opt) && requiredSelectionCount && selectedAnswers.length >= requiredSelectionCount) {
            return;
        }

        updateQuestionResponse(
            questionIndex,
                {
                    selectedOptions: selectedAnswers.includes(opt)
                        ? selectedAnswers.filter((answer) => answer !== opt)
                        : [...selectedAnswers, opt],
                    textAnswer: response?.textAnswer || '',
                }
            );
            return;
        }

        if (!isMultiSelectQuestion(question)) {
            updateQuestionResponse(questionIndex, opt);
            return;
        }

        const selectedAnswers = Array.isArray(response) ? response : [];
        const requiredSelectionCount = getRequiredSelectionCount(question);
        if (!selectedAnswers.includes(opt) && requiredSelectionCount && selectedAnswers.length >= requiredSelectionCount) {
            return;
        }

        updateQuestionResponse(
            questionIndex,
            selectedAnswers.includes(opt)
                ? selectedAnswers.filter((answer) => answer !== opt)
                : [...selectedAnswers, opt]
        );
    };

    const renderOptionsQuestion = (question, questionIndex, response, highlightIncomplete = false, retrySectionState = {}) => (
        <div style={{ ...getAnswerAreaStyles(highlightIncomplete), display: 'flex', flexWrap: 'wrap', gap: '20px', alignItems: 'flex-start' }}>
            <div style={{ flex: '1 1 320px', minWidth: '280px', width: '100%' }}>
                {question.options.map((opt, optionIndex) => {
                    const isChecked = isHybridQuestion(question)
                        ? (isMultiSelectQuestion(question)
                            ? (Array.isArray(response?.selectedOptions) && response.selectedOptions.includes(opt))
                            : response?.selectedOptions === opt)
                        : (isMultiSelectQuestion(question) ? (Array.isArray(response) && response.includes(opt)) : response === opt);
                    const isKeyAnswer = assessorMode && (
                        (Array.isArray(question.answers) && question.answers.includes(opt))
                        || question.answer === opt
                    );
                    const optionPrefix = assessorMode ? `${getOptionLabel(optionIndex)} ` : '';

                    return (
                        <div
                            key={opt}
                            style={{
                                display: 'grid',
                                gridTemplateColumns: usePdfTheme ? '34px minmax(0, 1fr)' : '26px minmax(0, 1fr)',
                                columnGap: '12px',
                                alignItems: 'start',
                                marginBottom: '18px',
                                width: '100%',
                            }}
                        >
                            <input
                                type="checkbox"
                                checked={isChecked}
                                disabled={readOnlyStudentResponses}
                                onChange={() => {
                                    if (readOnlyStudentResponses) {
                                        return;
                                    }
                                    toggleOptionValue(question, questionIndex, response, opt);
                                }}
                                style={{
                                    width: usePdfTheme ? '24px' : '18px',
                                    height: usePdfTheme ? '24px' : '18px',
                                    marginTop: usePdfTheme ? '4px' : '1px',
                                    marginLeft: 0,
                                    accentColor: '#1a3d6b',
                                    justifySelf: 'start',
                                }}
                            />
                            <label
                                onClick={() => {
                                    if (readOnlyStudentResponses) {
                                        return;
                                    }
                                    toggleOptionValue(question, questionIndex, response, opt);
                                }}
                                style={{
                                    cursor: readOnlyStudentResponses ? 'default' : 'pointer',
                                    lineHeight: 1.45,
                                    color: isKeyAnswer ? pdfAnswerRed : '#111',
                                    fontSize: usePdfTheme ? '17px' : 'inherit',
                                    fontStyle: usePdfTheme ? 'italic' : 'normal',
                                    margin: 0,
                                    display: 'block',
                                }}
                            >
                                {optionPrefix ? <span style={{ display: 'inline-block', minWidth: '38px' }}>{optionPrefix}</span> : null}
                                <span>{opt}</span>
                            </label>
                        </div>
                    );
                })}
            </div>
            {question.img && <div style={{ flex: '0 0 auto', width: question.imgWidth || '190px', maxWidth: '100%' }}>
                <img
                    src={question.img}
                    alt={question.imgAlt || `Question ${question.id}`}
                    style={{
                        width: '100%',
                        maxWidth: question.imgMaxWidth || question.imgWidth || '190px',
                        height: question.imgHeight || 'auto',
                        display: 'block',
                    }}
                />
            </div>}
            {question.partBQuestion && <div style={{ width: '100%', marginTop: '20px', ...(retrySectionState.highlightPartB ? { padding: '10px 12px', border: '1px solid #ef9a9a', backgroundColor: '#fff5f5', borderRadius: '8px' } : {}) }}>
                <p style={{ ...partSectionLabelStyles, marginBottom: retrySectionState.highlightPartB ? '6px' : '10px' }}>{question.partBLabel || 'Part B'}</p>
                {retrySectionState.highlightPartB && <p style={{ marginTop: 0, marginBottom: '10px', color: '#b42318', fontWeight: '700' }}>Incorrect answer. Please review this section.</p>}
                <p style={{ ...questionPromptTextStyles, marginBottom: '10px' }}>{question.partBQuestion}</p>
                <textarea
                    value={response?.textAnswer || ''}
                    onChange={({ target }) => {
                        autoResizeTextarea(target);
                        updateQuestionResponse(questionIndex, {
                            ...(response || {}),
                            selectedOptions: response?.selectedOptions || (isMultiSelectQuestion(question) ? [] : ''),
                            textAnswer: target.value,
                        });
                    }}
                    rows={6}
                    style={{ ...getQuestionTextareaStyles(question, '700px'), minHeight: '160px', borderColor: pdfBorder }}
                    ref={autoResizeTextarea}
                />
            </div>}
        </div>
    );

    const renderFormQuestion = (question, questionIndex, response, highlightIncomplete = false, options = {}) => {
        const { guideMode = false } = options;
        const trainerFeedback = guideMode ? {} : (reviewFeedbackData?.[questionIndex] || {});

        return (
            <div style={getAnswerAreaStyles(highlightIncomplete)}>
                {question.img && <div style={{ marginBottom: '20px' }}>
                    <img
                        src={question.img}
                        alt={question.imgAlt || `Question ${question.id}`}
                        style={{
                            width: question.imgWidth || '100%',
                            maxWidth: question.imgMaxWidth || '1000px',
                            height: 'auto',
                            display: 'block'
                        }}
                    />
                </div>}
                {question.options.map((row, index) => {
                    if (row.type === 'section') {
                        return (
                            <div key={`${row.name}-${index}`} style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap', color: 'black', fontWeight: '600', marginTop: index === 0 ? 0 : '18px', marginBottom: '10px' }}>
                                <p style={{ margin: 0 }}>{renderFormattedText(row.name, `form-section-${questionIndex}-${index}`)}</p>
                                {row.inlineImage && (
                                    <img
                                        src={row.inlineImage}
                                        alt={row.inlineImageAlt || `Question ${question.id} prompt`}
                                        style={{ width: row.inlineImageWidth || '140px', maxWidth: '100%', height: 'auto', display: 'block', flex: '0 0 auto' }}
                                    />
                                )}
                            </div>
                        );
                    }

                    const reviewKey = getFormRowReviewKey(index);
                    const rowMarkedIncorrect = studentRetryMode && getRowReviewMark(trainerFeedback, reviewKey) === 'NYC';
                    const rowReviewComment = getRowReviewComment(trainerFeedback, reviewKey);
                    const sharedInput = readOnlyStudentResponses
                        ? renderReadOnlyFieldValue(response?.[index], { minHeight: row.type === 'textarea' ? `${Math.max((row.rows || 4) * 24, 44)}px` : '44px' })
                        : renderFieldInput(
                            row.type,
                            response?.[index],
                            (nextValue) => updateQuestionResponse(questionIndex, { ...(response || {}), [index]: nextValue }),
                            row.rows || 4,
                            '1000px',
                            [],
                            { disableResize: question?.disableTextareaResize, error: rowMarkedIncorrect }
                        );

                    if (row.type === 'text') {
                        return (
                            <div key={`${row.name}-${index}`} style={{ display: 'grid', gridTemplateColumns: assessorMode && !guideMode ? 'minmax(140px, 220px) minmax(0, 1fr) 92px' : 'minmax(140px, 220px) minmax(0, 1fr)', columnGap: '14px', alignItems: 'start', marginBottom: '14px' }}>
                                <p style={{ margin: 0, paddingTop: '10px' }}>{renderFormattedText(row.name, `form-label-${questionIndex}-${index}`)}</p>
                                <div>
                                    {sharedInput}
                                    {renderRowRetryFeedback(rowMarkedIncorrect, rowReviewComment)}
                                </div>
                                {assessorMode && !guideMode ? renderAssessorRowMarkControl(questionIndex, reviewKey, true) : null}
                            </div>
                        );
                    }

                    if (assessorMode && !guideMode) {
                        return <div key={`${row.name}-${index}`} style={{ marginBottom: '14px' }}>
                            {row.name || row.inlineImage ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap', marginBottom: '10px' }}>
                                    {row.name ? <p style={{ margin: 0 }}>{renderFormattedText(row.name, `form-label-${questionIndex}-${index}`)}</p> : null}
                                    {row.inlineImage ? (
                                        <img
                                            src={row.inlineImage}
                                            alt={row.inlineImageAlt || `Question ${question.id} prompt`}
                                            style={{ width: row.inlineImageWidth || '140px', maxWidth: '100%', height: 'auto', display: 'block', flex: '0 0 auto' }}
                                        />
                                    ) : null}
                                </div>
                            ) : null}
                            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 92px', columnGap: '14px', alignItems: 'start' }}>
                                <div>
                                    {sharedInput}
                                    {renderRowRetryFeedback(rowMarkedIncorrect, rowReviewComment)}
                                </div>
                                {renderAssessorRowMarkControl(questionIndex, reviewKey, true, { marginTop: '16px' })}
                            </div>
                        </div>;
                    }

                    return <div key={`${row.name}-${index}`} style={{ marginBottom: '14px' }}>
                        <div>
                            <div>
                                {row.name || row.inlineImage ? (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap', marginBottom: '10px' }}>
                                        {row.name ? <p style={{ margin: 0 }}>{renderFormattedText(row.name, `form-label-${questionIndex}-${index}`)}</p> : null}
                                        {row.inlineImage ? (
                                            <img
                                                src={row.inlineImage}
                                                alt={row.inlineImageAlt || `Question ${question.id} prompt`}
                                                style={{ width: row.inlineImageWidth || '140px', maxWidth: '100%', height: 'auto', display: 'block', flex: '0 0 auto' }}
                                            />
                                        ) : null}
                                    </div>
                                ) : null}
                                {sharedInput}
                                {renderRowRetryFeedback(rowMarkedIncorrect, rowReviewComment)}
                            </div>
                        </div>
                    </div>;
                })}
            </div>
        );
    };

    const renderTableQuestion = (question, questionIndex, response, highlightIncomplete = false) => (
        <div style={getAnswerAreaStyles(highlightIncomplete)}>
            <p className="legendLeftPush" style={{ whiteSpace: 'pre' }}>
                {renderFormattedText(question.legend, `table-legend-${questionIndex}`)}
            </p>
            <table>
                <thead>
                    <tr>
                        {question.table.header.map((content) => <th key={content}>{content}</th>)}
                    </tr>
                </thead>
                <tbody>
                    {question.table.body.map((row, index) => <tr key={row.name}>
                        <td style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                            <img src={row.image} alt={row.name || `Question ${question.id} option ${index + 1}`} style={{ width: '150px', height: '150px' }} />
                            <label style={{ textAlign: 'center', width: '90%' }}>{row.name}</label>
                        </td>
                        <td>
                            <AssessmentSelect
                                options={question.options.map((value) => ({ label: value, value }))}
                                value={response?.[index] || ''}
                                setValue={(value) => updateQuestionResponse(questionIndex, { ...(response || {}), [index]: value.value })}
                            />
                        </td>
                    </tr>)}
                </tbody>
            </table>
        </div>
    );

    const renderTableFormQuestion = (question, questionIndex, response, options = {}) => {
        const { guideMode = false } = options;
        const activeTableFormHeader = assessorMode
            ? (question.tableForm.assessorHeader || question.tableForm.header || [])
            : (question.tableForm.studentHeader || question.tableForm.header || []);
        const isSingleColumnTableForm = activeTableFormHeader.length === 1;
        const trainerFeedback = guideMode ? {} : (reviewFeedbackData?.[questionIndex] || {});

        return (
            <div style={{ width: '100%', maxWidth: '1000px', overflowX: 'auto' }}>
                {assessorMode && !guideMode && ((embeddedMarkAllCMode && hasEmbeddedAssessorControls(question)) || (rightAlignedMarkingMode && question.assessorMarkPerRow)) && <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
                    <button
                        type='button'
                        onClick={markAllQuestionsCompetent}
                        style={{
                            border: `1px solid ${Green}`,
                            background: '#eaf7ed',
                            color: Green,
                            fontWeight: '700',
                            padding: '10px 14px',
                            borderRadius: '8px',
                            cursor: 'pointer',
                        }}
                    >
                        Mark All as C
                    </button>
                </div>}
                <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                    {activeTableFormHeader?.length > 0 && <thead>
                        <tr>
                            {activeTableFormHeader.map((content, index) => <th key={`${content}-${index}`} style={{ textAlign: 'left', padding: '10px', borderBottom: '1px solid #d9d9d9', backgroundColor: question.tableForm.headerBackgroundColor || '#fff', color: question.tableForm.headerTextColor || '#111', width: question.tableForm.columnWidths?.[index] || `${100 / activeTableFormHeader.length}%` }}>
                                {renderFormattedText(content, `table-form-header-${questionIndex}-${index}`)}
                            </th>)}
                            {assessorMode && rightAlignedMarkingMode && !guideMode && question.assessorMarkPerRow && <th style={{ textAlign: 'center', padding: '10px', borderBottom: '1px solid #d9d9d9', backgroundColor: question.tableForm.headerBackgroundColor || '#fff', color: question.tableForm.headerTextColor || '#111', width: '110px' }}>
                                Mark
                            </th>}
                        </tr>
                    </thead>}
                    <tbody>
                        {question.tableForm.rows.map((row, rowIndex) => {
                            if (row.assessorOnly && (!assessorMode || rightAlignedMarkingMode || guideMode)) {
                                return null;
                            }

                            const reviewKey = getTableRowReviewKey(rowIndex);
                            const rowReviewKeys = getTableFormRowReviewKeysForRow(question, row, rowIndex);
                            const rowMarkedIncorrect = studentRetryMode && rowReviewKeys.some((key) => getRowReviewMark(trainerFeedback, key) === 'NYC');
                            const rowReviewComment = rowReviewKeys.length === 1 ? getRowReviewComment(trainerFeedback, rowReviewKeys[0]) : '';

                            if (row.type === 'section') {
                                return <tr key={`table-form-section-${questionIndex}-${rowIndex}`}>
                                    <td colSpan={activeTableFormHeader.length || 2} style={{ padding: '12px 10px', backgroundColor: '#f7f7f7', borderBottom: '1px solid #ececec', fontWeight: '600' }}>
                                        {renderFormattedText(row.label, `table-form-section-${questionIndex}-${rowIndex}`)}
                                    </td>
                                </tr>;
                            }

                            if (row.type === 'static') {
                                return <tr key={`table-form-static-${questionIndex}-${rowIndex}`}>
                                    <td style={{ padding: '12px 10px', borderBottom: '1px solid #ececec', fontWeight: row.labelBold ? '600' : '400' }}>
                                        {renderFormattedText(row.label, `table-form-static-label-${questionIndex}-${rowIndex}`)}
                                        {renderRowRetryFeedback(rowMarkedIncorrect, rowReviewComment)}
                                    </td>
                                    {!isSingleColumnTableForm && <td style={{ padding: '12px 10px', borderBottom: '1px solid #ececec' }}>
                                        {row.assessorField && assessorMode
                                            ? renderFieldInput(
                                                row.assessorField.inputType || 'select',
                                                response?.[row.assessorField.key],
                                                (nextValue) => updateQuestionResponse(questionIndex, { ...(response || {}), [row.assessorField.key]: nextValue }),
                                                row.assessorField.rows || 4,
                                                '100%',
                                                row.assessorField.options || []
                                            )
                                            : renderFormattedText(row.value, `table-form-static-value-${questionIndex}-${rowIndex}`)}
                                    </td>}
                                </tr>;
                            }

                            if (row.type === 'inlineFields') {
                                return <tr key={`table-form-inline-${questionIndex}-${rowIndex}`}>
                                    <td style={{ padding: '12px 10px', verticalAlign: 'top', borderBottom: '1px solid #ececec' }}>
                                        {renderFormattedText(row.label, `table-form-inline-label-${questionIndex}-${rowIndex}`)}
                                        {!isGranularTableFormQuestion(question) && renderRowRetryFeedback(rowMarkedIncorrect, rowReviewComment)}
                                        {isSingleColumnTableForm && <div style={{ marginTop: '10px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                            {row.fields.map((field) => {
                                                const fieldReviewKey = isGranularTableFormQuestion(question) ? getTableFormFieldReviewKey(rowIndex, field.key) : reviewKey;
                                                const fieldMarkedIncorrect = studentRetryMode && getRowReviewMark(trainerFeedback, fieldReviewKey) === 'NYC';
                                                const fieldReviewComment = getRowReviewComment(trainerFeedback, fieldReviewKey);

                                                return <div key={field.key} style={{ flex: field.flex || '1 1 160px', minWidth: field.minWidth || '140px' }}>
                                                    <p style={{ marginBottom: '6px', color: '#666', fontSize: '14px' }}>{field.label}</p>
                                                    {readOnlyStudentResponses
                                                        ? renderReadOnlyFieldValue(response?.[field.key], {
                                                            fieldType: field.type || 'text',
                                                            fieldOptions: field.options || [],
                                                        })
                                                        : renderFieldInput(
                                                            field.type || 'text',
                                                            response?.[field.key],
                                                            (nextValue) => updateQuestionResponse(questionIndex, { ...(response || {}), [field.key]: nextValue }),
                                                            field.rows || 4,
                                                            '100%',
                                                            field.options || [],
                                                            { error: fieldMarkedIncorrect }
                                                        )}
                                                    {assessorMode && rightAlignedMarkingMode && !guideMode && isGranularTableFormQuestion(question) && renderAssessorRowMarkControl(questionIndex, fieldReviewKey, true, { marginTop: '10px', marginLeft: 'auto' })}
                                                    {renderRowRetryFeedback(
                                                        fieldMarkedIncorrect,
                                                        fieldReviewComment,
                                                        {
                                                            hideDefaultMessage: isGranularTableFormQuestion(question),
                                                            customMessage: isGranularTableFormQuestion(question) ? 'Please answer this correctly.' : '',
                                                        }
                                                    )}
                                                </div>;
                                            })}
                                        </div>}
                                    </td>
                                    {!isSingleColumnTableForm && <td style={{ padding: '12px 10px', borderBottom: '1px solid #ececec' }}>
                                        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                            {row.fields.map((field) => {
                                                const fieldReviewKey = isGranularTableFormQuestion(question) ? getTableFormFieldReviewKey(rowIndex, field.key) : reviewKey;
                                                const fieldMarkedIncorrect = studentRetryMode && getRowReviewMark(trainerFeedback, fieldReviewKey) === 'NYC';
                                                const fieldReviewComment = getRowReviewComment(trainerFeedback, fieldReviewKey);

                                                return <div key={field.key} style={{ flex: field.flex || '1 1 160px', minWidth: field.minWidth || '140px' }}>
                                                    <p style={{ marginBottom: '6px', color: '#666', fontSize: '14px' }}>{field.label}</p>
                                                    {readOnlyStudentResponses
                                                        ? renderReadOnlyFieldValue(response?.[field.key], {
                                                            fieldType: field.type || 'text',
                                                            fieldOptions: field.options || [],
                                                        })
                                                        : renderFieldInput(
                                                            field.type || 'text',
                                                            response?.[field.key],
                                                            (nextValue) => updateQuestionResponse(questionIndex, { ...(response || {}), [field.key]: nextValue }),
                                                            field.rows || 4,
                                                            '100%',
                                                            field.options || [],
                                                            { error: fieldMarkedIncorrect }
                                                        )}
                                                    {assessorMode && rightAlignedMarkingMode && !guideMode && isGranularTableFormQuestion(question) && renderAssessorRowMarkControl(questionIndex, fieldReviewKey, true, { marginTop: '10px', marginLeft: 'auto' })}
                                                    {renderRowRetryFeedback(
                                                        fieldMarkedIncorrect,
                                                        fieldReviewComment,
                                                        {
                                                            hideDefaultMessage: isGranularTableFormQuestion(question),
                                                            customMessage: isGranularTableFormQuestion(question) ? 'Please answer this correctly.' : '',
                                                        }
                                                    )}
                                                </div>;
                                            })}
                                        </div>
                                    </td>}
                                    {assessorMode && rightAlignedMarkingMode && !guideMode && <td style={{ padding: '12px 10px', borderBottom: '1px solid #ececec', verticalAlign: 'top' }}>
                                        {renderAssessorRowMarkControl(questionIndex, rowReviewKeys.length > 0 ? rowReviewKeys : reviewKey, true)}
                                    </td>}
                                </tr>;
                            }

                            return <tr key={`table-form-row-${questionIndex}-${rowIndex}`}>
                                <td style={{ padding: '12px 10px', verticalAlign: 'top', borderBottom: '1px solid #ececec' }}>
                                    {renderFormattedText(row.label, `table-form-label-${questionIndex}-${rowIndex}`)}
                                    {!isGranularTableFormQuestion(question) && renderRowRetryFeedback(rowMarkedIncorrect, rowReviewComment)}
                                    {isSingleColumnTableForm && <div style={{ marginTop: '10px' }}>
                                        {(() => {
                                            const fieldReviewKey = isGranularTableFormQuestion(question) ? getTableFormFieldReviewKey(rowIndex, row.key) : reviewKey;
                                            const fieldMarkedIncorrect = studentRetryMode && getRowReviewMark(trainerFeedback, fieldReviewKey) === 'NYC';
                                            const fieldReviewComment = getRowReviewComment(trainerFeedback, fieldReviewKey);

                                            return <>
                                                {readOnlyStudentResponses
                                                    ? renderReadOnlyFieldValue(response?.[row.key], { minHeight: row.inputType === 'textarea' || !row.inputType ? `${Math.max((row.rows || 4) * 24, 44)}px` : '44px' })
                                                    : renderFieldInput(
                                                        row.inputType || 'textarea',
                                                        response?.[row.key],
                                                        (nextValue) => updateQuestionResponse(questionIndex, { ...(response || {}), [row.key]: nextValue }),
                                                        row.rows || 4,
                                                        '100%',
                                                        row.options || [],
                                                        { disableResize: question?.disableTextareaResize, error: fieldMarkedIncorrect }
                                                    )}
                                                {assessorMode && rightAlignedMarkingMode && !guideMode && isGranularTableFormQuestion(question) && renderAssessorRowMarkControl(questionIndex, fieldReviewKey, true, { marginTop: '10px', marginLeft: 'auto' })}
                                                {renderRowRetryFeedback(
                                                    fieldMarkedIncorrect,
                                                    fieldReviewComment,
                                                    {
                                                        hideDefaultMessage: isGranularTableFormQuestion(question),
                                                        customMessage: isGranularTableFormQuestion(question) ? 'Please answer this correctly.' : '',
                                                    }
                                                )}
                                            </>;
                                        })()}
                                    </div>}
                                </td>
                                {!isSingleColumnTableForm && <td style={{ padding: '12px 10px', borderBottom: '1px solid #ececec' }}>
                                    {(() => {
                                        const fieldReviewKey = isGranularTableFormQuestion(question) ? getTableFormFieldReviewKey(rowIndex, row.key) : reviewKey;
                                        const fieldMarkedIncorrect = studentRetryMode && getRowReviewMark(trainerFeedback, fieldReviewKey) === 'NYC';
                                        const fieldReviewComment = getRowReviewComment(trainerFeedback, fieldReviewKey);

                                        return <>
                                            {readOnlyStudentResponses
                                                ? renderReadOnlyFieldValue(response?.[row.key], { minHeight: row.inputType === 'textarea' || !row.inputType ? `${Math.max((row.rows || 4) * 24, 44)}px` : '44px' })
                                                : renderFieldInput(
                                                    row.inputType || 'textarea',
                                                    response?.[row.key],
                                                    (nextValue) => updateQuestionResponse(questionIndex, { ...(response || {}), [row.key]: nextValue }),
                                                    row.rows || 4,
                                                    '100%',
                                                    row.options || [],
                                                    { disableResize: question?.disableTextareaResize, error: fieldMarkedIncorrect }
                                                )}
                                            {assessorMode && rightAlignedMarkingMode && !guideMode && isGranularTableFormQuestion(question) && renderAssessorRowMarkControl(questionIndex, fieldReviewKey, true, { marginTop: '10px', marginLeft: 'auto' })}
                                            {renderRowRetryFeedback(
                                                fieldMarkedIncorrect,
                                                fieldReviewComment,
                                                {
                                                    hideDefaultMessage: isGranularTableFormQuestion(question),
                                                    customMessage: isGranularTableFormQuestion(question) ? 'Please answer this correctly.' : '',
                                                }
                                            )}
                                        </>;
                                    })()}
                                </td>}
                                {assessorMode && rightAlignedMarkingMode && !guideMode && <td style={{ padding: '12px 10px', borderBottom: '1px solid #ececec', verticalAlign: 'top' }}>
                                    {renderAssessorRowMarkControl(questionIndex, rowReviewKeys.length > 0 ? rowReviewKeys : reviewKey, true)}
                                </td>}
                            </tr>;
                        })}
                    </tbody>
                </table>
            </div>
        );
    };

    const renderImageTableFormQuestion = (question, questionIndex, response, retrySectionState = {}, options = {}) => {
        const { guideMode = false } = options;
        const tableAnswers = question.partBFields?.length ? response?.tableAnswers || {} : response || {};
        const partBAnswers = question.partBFields?.length ? response?.partBAnswers || {} : {};
        const trainerFeedback = guideMode ? {} : (reviewFeedbackData?.[questionIndex] || {});
        const showAssessorRowMarks = assessorMode && !guideMode && question.assessorMarkPerRow;

        return (
            <div style={{ width: '100%', maxWidth: '1000px', overflowX: 'auto', ...(retrySectionState.highlightPartA ? { padding: '10px 12px', border: '1px solid #ef9a9a', backgroundColor: '#fff5f5', borderRadius: '8px' } : {}) }}>
                {retrySectionState.highlightPartA && <p style={{ marginTop: 0, marginBottom: '12px', color: '#b42318', fontWeight: '700' }}>Incorrect answer. Please review this section.</p>}
                <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                    <thead>
                        {question.shortAnswerHeading && (
                            <tr>
                                <th
                                    colSpan={question.table.header.length + (showAssessorRowMarks ? 1 : 0)}
                                    style={{ textAlign: 'center', backgroundColor: '#f1f1f1', padding: '12px 10px', borderBottom: '1px solid #d9d9d9', fontSize: '23px', color: '#111' }}
                                >
                                    {question.shortAnswerHeading}
                                </th>
                            </tr>
                        )}
                        <tr>
                            {question.table.header.map((content, index) => <th key={content} style={{ textAlign: question.tableHeaderTextAlign || 'left', backgroundColor: question.tableHeaderBackgroundColor || 'transparent', padding: '10px', borderBottom: '1px solid #d9d9d9', width: index === 0 ? '22%' : `${78 / (question.table.header.length - 1)}%` }}>
                                {content}
                            </th>)}
                            {showAssessorRowMarks && <th style={{ textAlign: 'center', backgroundColor: question.tableHeaderBackgroundColor || '#f1f1f1', padding: '10px', borderBottom: '1px solid #d9d9d9', width: '120px' }}>
                                Mark
                            </th>}
                        </tr>
                    </thead>
                    <tbody>
                        {question.table.body.map((row, rowIndex) => {
                            const rowReviewKeys = getImageTableRowReviewKeys(question, rowIndex);

                            return <tr key={row.image}>
                                <td style={{ padding: '12px 10px', verticalAlign: 'top', borderBottom: '1px solid #ececec' }}>
                                    <img src={row.image} alt={row.alt || `Question ${question.id} row ${rowIndex + 1}`} style={{ width: '100%', maxWidth: '140px', height: 'auto', aspectRatio: '1 / 1', objectFit: 'contain', display: 'block' }} />
                                </td>
                                {question.inputColumns.map((column) => {
                                    const reviewKey = isGranularImageTableQuestion(question)
                                        ? getImageTableCellReviewKey(rowIndex, column.key)
                                        : rowReviewKeys[0];
                                    const fieldMarkedIncorrect = studentRetryMode && getRowReviewMark(trainerFeedback, reviewKey) === 'NYC';
                                    const fieldReviewComment = getRowReviewComment(trainerFeedback, reviewKey);
                                    const textareaStyle = getQuestionTextareaStyles(question);

                                    return <td key={column.key} style={{ padding: '12px 10px', verticalAlign: 'top', borderBottom: '1px solid #ececec' }}>
                                        {readOnlyStudentResponses
                                            ? renderReadOnlyFieldValue(tableAnswers?.[rowIndex]?.[column.key], { minHeight: `${Math.max((column.rows || 4) * 24, 44)}px` })
                                            : <textarea
                                                value={tableAnswers?.[rowIndex]?.[column.key] || ''}
                                                onChange={({ target }) => {
                                                    autoResizeTextarea(target);
                                                    updateQuestionResponse(questionIndex, question.partBFields?.length
                                                        ? {
                                                            tableAnswers: {
                                                                ...tableAnswers,
                                                                [rowIndex]: {
                                                                    ...(tableAnswers[rowIndex] || {}),
                                                                    [column.key]: target.value,
                                                                }
                                                            },
                                                            partBAnswers,
                                                        }
                                                        : {
                                                            ...tableAnswers,
                                                            [rowIndex]: {
                                                                ...(tableAnswers[rowIndex] || {}),
                                                                [column.key]: target.value,
                                                                }
                                                        });
                                                }}
                                                rows={column.rows || 4}
                                                style={{ ...textareaStyle, borderColor: fieldMarkedIncorrect ? '#c62828' : textareaStyle.borderColor }}
                                                ref={autoResizeTextarea}
                                            />}
                                        {assessorMode && !guideMode && isGranularImageTableQuestion(question) && renderAssessorRowMarkControl(questionIndex, reviewKey, true, { marginTop: '10px', marginLeft: 'auto' })}
                                        {renderRowRetryFeedback(
                                            fieldMarkedIncorrect,
                                            fieldReviewComment,
                                            {
                                                hideDefaultMessage: isGranularImageTableQuestion(question),
                                                customMessage: isGranularImageTableQuestion(question) ? 'Please answer this correctly.' : '',
                                            }
                                        )}
                                    </td>;
                                })}
                                {showAssessorRowMarks && <td style={{ padding: '12px 10px', verticalAlign: 'top', borderBottom: '1px solid #ececec' }}>
                                    {renderAssessorRowMarkControl(questionIndex, rowReviewKeys, true, { marginTop: '10px', marginLeft: 'auto' })}
                                </td>}
                            </tr>;
                        })}
                    </tbody>
                </table>
                {question.partBFields?.length > 0 && <div style={{ width: '100%', marginTop: '20px', ...(retrySectionState.highlightPartB ? { padding: '10px 12px', border: '1px solid #ef9a9a', backgroundColor: '#fff5f5', borderRadius: '8px' } : {}) }}>
                    <p style={{ ...partSectionLabelStyles, marginBottom: retrySectionState.highlightPartB ? '6px' : '10px' }}>{renderFormattedText(question.partBLabel || 'Part B', `part-b-label-${questionIndex}`)}</p>
                    {retrySectionState.highlightPartB && <p style={{ marginTop: 0, marginBottom: '10px', color: '#b42318', fontWeight: '700' }}>Incorrect answer. Please review this section.</p>}
                    {question.partBFields.map((field, index) => {
                        const reviewKey = getPartBRowReviewKey(index);
                        const rowMarkedIncorrect = studentRetryMode && getRowReviewMark(trainerFeedback, reviewKey) === 'NYC';
                        const rowReviewComment = getRowReviewComment(trainerFeedback, reviewKey);

                        if (showAssessorRowMarks) {
                            return <div key={`${field.name}-${index}`} style={{ marginBottom: '14px' }}>
                                <p style={{ marginBottom: '10px' }}>{renderFormattedText(field.name, `part-b-field-${questionIndex}-${index}`)}</p>
                                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 92px', columnGap: '14px', alignItems: 'start' }}>
                                    <div>
                                        {readOnlyStudentResponses
                                            ? renderReadOnlyFieldValue(partBAnswers?.[index], { minHeight: `${Math.max((field.rows || 4) * 24, 44)}px` })
                                            : <textarea
                                                value={partBAnswers?.[index] || ''}
                                                onChange={({ target }) => updateQuestionResponse(questionIndex, {
                                                    tableAnswers,
                                                    partBAnswers: {
                                                        ...partBAnswers,
                                                        [index]: target.value,
                                                    }
                                                })}
                                                rows={field.rows || 4}
                                                style={{
                                                    ...getQuestionTextareaStyles(question),
                                                    borderColor: rowMarkedIncorrect ? '#c62828' : getQuestionTextareaStyles(question).borderColor,
                                                }}
                                            />}
                                        {renderRowRetryFeedback(
                                            rowMarkedIncorrect,
                                            rowReviewComment,
                                            {
                                                hideDefaultMessage: isGranularImageTableQuestion(question),
                                                customMessage: isGranularImageTableQuestion(question) ? 'Please answer this correctly.' : '',
                                            }
                                        )}
                                    </div>
                                    {renderAssessorRowMarkControl(questionIndex, reviewKey, true, { marginTop: '16px' })}
                                </div>
                            </div>;
                        }

                        return <div key={`${field.name}-${index}`} style={{ marginBottom: '14px' }}>
                            <p style={{ marginBottom: '10px' }}>{renderFormattedText(field.name, `part-b-field-${questionIndex}-${index}`)}</p>
                            {readOnlyStudentResponses
                                ? renderReadOnlyFieldValue(partBAnswers?.[index], { minHeight: `${Math.max((field.rows || 4) * 24, 44)}px` })
                                : <textarea
                                    value={partBAnswers?.[index] || ''}
                                    onChange={({ target }) => updateQuestionResponse(questionIndex, {
                                        tableAnswers,
                                        partBAnswers: {
                                            ...partBAnswers,
                                            [index]: target.value,
                                        }
                                    })}
                                    rows={field.rows || 4}
                                    style={{
                                        ...getQuestionTextareaStyles(question),
                                        borderColor: rowMarkedIncorrect ? '#c62828' : getQuestionTextareaStyles(question).borderColor,
                                    }}
                                />}
                            {renderRowRetryFeedback(
                                rowMarkedIncorrect,
                                rowReviewComment,
                                {
                                    hideDefaultMessage: isGranularImageTableQuestion(question),
                                    customMessage: isGranularImageTableQuestion(question) ? 'Please answer this correctly.' : '',
                                }
                            )}
                        </div>;
                    })}
                </div>}
            </div>
        );
    };

    const renderTextTableFormQuestion = (question, questionIndex, response, options = {}) => {
        const { guideMode = false } = options;
        const trainerFeedback = guideMode ? {} : (reviewFeedbackData?.[questionIndex] || {});
        const showAssessorRowMarks = assessorMode && inlineMarkingMode && !guideMode;

        return (
            <div style={{ width: '100%', maxWidth: '1000px', overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                    <thead>
                        <tr>
                            {question.table.header.map((content, index) => <th key={content} style={{ textAlign: 'left', padding: '10px', borderBottom: '1px solid #d9d9d9', width: question.stackedInputColumns ? (showAssessorRowMarks ? (index === 0 ? '28%' : '56%') : (index === 0 ? '28%' : '72%')) : (showAssessorRowMarks ? (index === 0 ? '28%' : `${56 / (question.table.header.length - 1)}%`) : (index === 0 ? '28%' : `${72 / (question.table.header.length - 1)}%`)) }}>
                                {content}
                            </th>)}
                            {showAssessorRowMarks && <th style={{ textAlign: 'center', padding: '10px', borderBottom: '1px solid #d9d9d9', width: '16%' }}>
                                Mark
                            </th>}
                        </tr>
                    </thead>
                    <tbody>
                        {question.table.body.map((row, rowIndex) => {
                            const reviewKey = getTableRowReviewKey(rowIndex);
                            const rowMarkedIncorrect = studentRetryMode && getRowReviewMark(trainerFeedback, reviewKey) === 'NYC';
                            const rowReviewComment = getRowReviewComment(trainerFeedback, reviewKey);

                            return <tr key={row.label}>
                                <td style={{ padding: '12px 10px', verticalAlign: 'top', borderBottom: '1px solid #ececec' }}>
                                    {renderFormattedText(row.label, `text-table-row-${questionIndex}-${rowIndex}`)}
                                    {renderRowRetryFeedback(rowMarkedIncorrect, rowReviewComment)}
                                </td>
                                {question.stackedInputColumns ? (
                                    <td style={{ padding: '12px 10px', verticalAlign: 'top', borderBottom: '1px solid #ececec' }}>
                                        {question.inputColumns.map((column, columnIndex) => <div key={column.key} style={{ display: 'grid', gridTemplateColumns: '22px minmax(0, 1fr)', columnGap: '8px', alignItems: 'start', marginBottom: columnIndex === question.inputColumns.length - 1 ? 0 : '12px' }}>
                                            <div style={{ paddingTop: '10px', fontWeight: '600' }}>{columnIndex + 1}.</div>
                                            <div>
                                                {readOnlyStudentResponses
                                                    ? renderReadOnlyFieldValue(response?.[rowIndex]?.[column.key], { minHeight: `${Math.max((column.rows || 4) * 24, 44)}px` })
                                                    : <textarea
                                                        value={response?.[rowIndex]?.[column.key] || ''}
                                                        onChange={({ target }) => {
                                                            autoResizeTextarea(target);
                                                            updateQuestionResponse(questionIndex, {
                                                                ...(response || {}),
                                                                [rowIndex]: {
                                                                    ...((response || {})[rowIndex] || {}),
                                                                    [column.key]: target.value,
                                                                }
                                                            });
                                                        }}
                                                        rows={column.rows || 4}
                                                        style={{ ...textareaStyles, resize: question?.disableTextareaResize ? 'none' : textareaStyles.resize }}
                                                        ref={autoResizeTextarea}
                                                    />}
                                            </div>
                                        </div>)}
                                    </td>
                                ) : question.inputColumns.map((column) => <td key={column.key} style={{ padding: '12px 10px', verticalAlign: 'top', borderBottom: '1px solid #ececec' }}>
                                    {readOnlyStudentResponses
                                        ? renderReadOnlyFieldValue(response?.[rowIndex]?.[column.key], { minHeight: `${Math.max((column.rows || 4) * 24, 44)}px` })
                                        : <textarea
                                            value={response?.[rowIndex]?.[column.key] || ''}
                                            onChange={({ target }) => {
                                                autoResizeTextarea(target);
                                                updateQuestionResponse(questionIndex, {
                                                    ...(response || {}),
                                                    [rowIndex]: {
                                                        ...((response || {})[rowIndex] || {}),
                                                        [column.key]: target.value,
                                                    }
                                                });
                                            }}
                                            rows={column.rows || 4}
                                            style={{ ...textareaStyles, resize: question?.disableTextareaResize ? 'none' : textareaStyles.resize }}
                                            ref={autoResizeTextarea}
                                        />}
                                </td>)}
                                {showAssessorRowMarks && <td style={{ padding: '12px 10px', verticalAlign: 'top', borderBottom: '1px solid #ececec' }}>
                                    {renderAssessorRowMarkControl(questionIndex, reviewKey, true, { marginTop: '10px', marginLeft: 'auto' })}
                                </td>}
                            </tr>;
                        })}
                    </tbody>
                </table>
            </div>
        );
    };

    const renderQuestion = (question, questionIndex) => {
        const response = getResponseForQuestion(questionIndex);
        const usesInlineRowLevelMarking = assessorMode
            && inlineMarkingMode
            && ['form', 'textTableForm'].includes(question?.type);
        const showAssessorMark = hasManualAnswerSection(question)
            && !usesInlineRowLevelMarking
            && !(rightAlignedMarkingMode && question?.assessorMarkPerRow)
            && (!hasEmbeddedAssessorControls(question) || rightAlignedMarkingMode);
        const trainerFeedback = reviewFeedbackData?.[questionIndex] || {};
        const trainerFeedbackComment = trainerFeedback.comment || response?.assessorComments || '';
        const trainerCommentValue = localAssessorReview?.questionMarks?.[questionIndex]?.comment || '';
        const shouldShowTrainerFeedback = studentRetryMode;
        const shouldShowTopTrainerFeedback = shouldShowTrainerFeedback && !shouldUseSectionOnlyRetryFeedback(question);
        const shouldShowAutoRetryFeedback = !assessorMode && answeringIncorrectQs && pendingIncorrectQuestionIndexes.includes(questionIndex);
        const highlightIncomplete = unansweredQuestionIds.includes(question.id);
        const shouldShowInlineAssessorGuide = assessorMode && inlineAssessorGuideMode && (question.assessorGuideHtml || question.assessorGuideTable || question.assessorGuideResponse);
        const hasTrainerMarkedIncorrect = trainerFeedback?.mark === 'NYC'
            || hasIncorrectRowMark(trainerFeedback)
            || response?.assessmentMark === 'NYC'
            || Object.keys(response || {}).some((key) => key.startsWith('assessorMark_') && response?.[key] === 'NYC');
        const shouldHighlightTrainerRetrySection = studentRetryMode && hasTrainerMarkedIncorrect;
        const retrySectionState = buildRetrySectionState(question, trainerFeedback, shouldShowTrainerFeedback);
        const trainerReviewSectionOptions = getQuestionReviewSectionOptions(question);

        const questionNameStyles = usePdfTheme
            ? {
                color: '#111',
                fontWeight: '600',
                fontSize: '17px',
                margin: 0,
                flex: '1 1 360px',
                lineHeight: 1.45,
            }
            : {
                color: 'black',
                fontWeight: 'normal',
                fontSize: '19px',
                margin: 0,
                flex: '1 1 360px',
            };

        return (
            <div
                key={`question-${question.id}`}
                className='questionBox'
                style={{
                    marginBottom: '20px',
                    border: usePdfTheme ? `1px solid ${pdfBorder}` : (emphasizeQuestionBorder || shouldHighlightTrainerRetrySection ? `2px solid ${shouldHighlightTrainerRetrySection ? '#c62828' : '#d9d9d9'}` : 'none'),
                    borderRadius: usePdfTheme ? 0 : (emphasizeQuestionBorder || shouldHighlightTrainerRetrySection ? '12px' : 0),
                    padding: usePdfTheme ? 0 : (emphasizeQuestionBorder || shouldHighlightTrainerRetrySection ? '18px' : 0),
                }}
            >
                {usePdfTheme && questionIndex === currentQuestionIndexes[0] && (
                    <div style={{ display: 'grid', gridTemplateColumns: assessorMode ? '1fr 74px' : '1fr', borderBottom: `1px solid ${pdfBorder}`, backgroundColor: pdfHeaderFill }}>
                        <div style={{ padding: '8px 10px', fontSize: '13px', fontWeight: '700', color: '#222' }}>Assessment</div>
                        {assessorMode && <div style={{ padding: '8px 10px', fontSize: '13px', fontWeight: '700', color: '#222', borderLeft: `1px solid ${pdfBorder}`, textAlign: 'center' }}>Mark<br />C or NYC</div>}
                    </div>
                )}
                <div style={usePdfTheme ? { padding: '12px 14px' } : undefined}>
                {!hideQuestionNumbers && <h4 className="questionNo" style={{ color: highlightIncomplete ? '#c62828' : (usePdfTheme ? '#111' : undefined), fontSize: usePdfTheme ? '17px' : undefined, fontWeight: usePdfTheme ? '600' : undefined, marginTop: 0 }}>
                    Question {question.id}
                    {highlightIncomplete && <span style={{ marginLeft: '10px', fontWeight: '700' }}>Not Yet Answered</span>}
                </h4>}
                {shouldShowAutoRetryFeedback && <div style={{ marginBottom: '16px', padding: '12px 14px', borderRadius: '8px', backgroundColor: '#fff7e6', border: '1px solid #ffd591', color: '#7a4b00', maxWidth: '1000px' }}>
                    <p style={{ margin: 0, fontWeight: '700', color: '#b42318' }}>Incorrect answer.</p>
                    <p style={{ margin: '8px 0 0', color: '#b42318' }}>Please review and update this response.</p>
                </div>}
                {shouldShowTopTrainerFeedback && <div style={{ marginBottom: '16px', padding: '12px 14px', borderRadius: '8px', backgroundColor: '#fff5f5', border: '1px solid #ef9a9a', color: '#8a1c1c', maxWidth: '1000px' }}>
                    <p style={{ margin: 0, fontWeight: '700' }}>This answer needs to be updated.</p>
                    {trainerFeedbackComment && <p style={{ margin: '8px 0 0' }}>Trainer note: {trainerFeedbackComment}</p>}
                </div>}
                {question.guide && <div style={{ border: `1px solid ${Green}`, marginBottom: '16px' }}>
                    <div style={{ color: 'white', backgroundColor: Green, width: 'calc(100% - 20px)', padding: '10px', cursor: 'pointer' }} onClick={() => setShowingGuide((previous) => ({ ...previous, [questionIndex]: !previous[questionIndex] }))}>
                        {showingGuide[questionIndex] ? '▲ Hide Answer Guide - Click Here ▲' : ' ▼ Answer Guide - Click Here ▼'}
                    </div>
                    {showingGuide[questionIndex] && <p style={{ margin: 0, padding: '10px', fontSize: '15px' }} dangerouslySetInnerHTML={{ __html: question.guide }} />}
                </div>}
                {question.partALabel && <div style={retrySectionState.highlightPartA ? { marginBottom: '12px', padding: '10px 12px', border: '1px solid #ef9a9a', backgroundColor: '#fff5f5', borderRadius: '8px' } : { marginBottom: '10px' }}>
                    <p style={{ ...partSectionLabelStyles, marginBottom: retrySectionState.highlightPartA ? '6px' : 0 }}>{renderFormattedText(question.partALabel, `part-a-label-${question.id}`)}</p>
                    {retrySectionState.highlightPartA && <p style={{ margin: 0, color: '#b42318', fontWeight: '700' }}>Incorrect answer. Please review this section.</p>}
                </div>}

                <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                    <h3 style={questionNameStyles}>{renderFormattedText(question.name, `question-name-${question.id}`)}</h3>
                    {assessorMode && inlineMarkingMode && showAssessorMark && <div style={{ width: '160px', flex: '0 0 160px' }}>
                        <label className='fieldHeading' style={{ marginTop: 0, marginBottom: '6px', display: 'block', textAlign: 'right' }}>Mark</label>
                        {renderFieldInput(
                            'select',
                            localAssessorReview?.questionMarks?.[questionIndex]?.mark || '',
                            (nextValue) => updateAssessorReview(questionIndex, 'mark', nextValue),
                            4,
                            '100%',
                            ['C', 'NYC']
                        )}
                        {trainerReviewSectionOptions.length > 0 && (localAssessorReview?.questionMarks?.[questionIndex]?.mark || '') === 'NYC' && (
                            <>
                                <label className='fieldHeading' style={{ marginTop: '10px', marginBottom: '6px', display: 'block', textAlign: 'right' }}>Review Section</label>
                                <select
                                    value={localAssessorReview?.questionMarks?.[questionIndex]?.reviewSection || getDefaultQuestionReviewSection(question)}
                                    onChange={({ target }) => updateAssessorReview(questionIndex, 'reviewSection', target.value)}
                                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ccc', fontFamily: 'inherit', fontSize: '15px', boxSizing: 'border-box', backgroundColor: '#fff', color: '#111' }}
                                >
                                    {trainerReviewSectionOptions.map((option) => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </>
                        )}
                    </div>}
                </div>
                {shouldShowInlineAssessorGuide && <div style={{ marginBottom: '16px', padding: '12px 14px', borderRadius: '8px', backgroundColor: '#fff5f5', border: '1px solid #ef9a9a', color: '#111', maxWidth: '1000px' }}>
                    {renderAssessorGuideContent(question)}
                </div>}
                {assessorMode && inlineMarkingMode && showMarkAllAsC && <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
                    <button
                        type='button'
                        onClick={markAllQuestionsCompetent}
                        style={{
                            border: `1px solid ${Green}`,
                            background: '#eaf7ed',
                            color: Green,
                            fontWeight: '700',
                            padding: '10px 14px',
                            borderRadius: '8px',
                            cursor: 'pointer',
                        }}
                    >
                        Mark All as C
                    </button>
                </div>}
                {question.type === 'options' && <p style={{ ...partSectionLabelStyles, fontSize: '15px', lineHeight: 1.45, marginBottom: '15px' }}>{renderFormattedSubtitle(question.subtitle || (!question.incorrect ? 'Tick (✓) the correct option.' : 'Tick (✓) the incorrect option.'))}</p>}
                {question.subtitle && question.type !== 'options' && question.subtitlePlacement !== 'afterInfoTable' && <p style={{ color: '#444', marginBottom: '15px', fontSize: usePdfTheme ? '19px' : undefined }}>{renderFormattedText(question.subtitle, `question-subtitle-${question.id}`)}</p>}
                {studentQuestionOnly && renderStudentPromptOnly(question)}
                {(assessorMode && question.assessorInfoTable ? question.assessorInfoTable : question.infoTable) && <div style={{ width: '100%', maxWidth: '1000px', overflowX: 'auto', marginBottom: '20px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                        <tbody>
                            {(assessorMode && question.assessorInfoTable ? question.assessorInfoTable : question.infoTable).rows.map((row, index) => <tr key={`info-table-${question.id}-${index}`}>
                                <td style={{ width: (assessorMode && question.assessorInfoTable ? question.assessorInfoTable : question.infoTable).columnWidths?.[0] || '28%', padding: '12px 10px', border: `1px solid ${pdfBorder}`, verticalAlign: 'top', fontWeight: '600', backgroundColor: pdfHeaderFill, fontSize: usePdfTheme ? '13px' : undefined }}>
                                    {renderFormattedText(row.label, `info-table-label-${question.id}-${index}`)}
                                </td>
                                <td style={{ width: (assessorMode && question.assessorInfoTable ? question.assessorInfoTable : question.infoTable).columnWidths?.[1] || '72%', padding: '12px 10px', border: `1px solid ${pdfBorder}`, verticalAlign: 'top', fontSize: usePdfTheme ? '13px' : undefined, lineHeight: usePdfTheme ? 1.45 : undefined }}>
                                    {row.valueHtml ? renderHtmlContent(row.valueHtml) : renderFormattedText(row.value, `info-table-value-${question.id}-${index}`)}
                                </td>
                            </tr>)}
                        </tbody>
                    </table>
                </div>}
                {question.subtitle && question.type !== 'options' && question.subtitlePlacement === 'afterInfoTable' && <p style={{ color: '#444', marginBottom: '15px', fontSize: usePdfTheme ? '19px' : undefined }}>{renderFormattedText(question.subtitle, `question-subtitle-after-table-${question.id}`)}</p>}
                {!studentQuestionOnly && question.type === 'form' && question.header && <h3>{question.header}</h3>}
                {!studentQuestionOnly && question.type === 'form' && renderFormQuestion(question, questionIndex, response, highlightIncomplete)}
                {!studentQuestionOnly && question.type === 'table' && renderTableQuestion(question, questionIndex, response, highlightIncomplete)}
                {!studentQuestionOnly && question.type === 'tableForm' && <div style={getAnswerAreaStyles(highlightIncomplete)}>{renderTableFormQuestion(question, questionIndex, response)}</div>}
                {!studentQuestionOnly && question.type === 'imageTableForm' && <div style={getAnswerAreaStyles(highlightIncomplete)}>{renderImageTableFormQuestion(question, questionIndex, response, retrySectionState)}</div>}
                {!studentQuestionOnly && question.type === 'textTableForm' && <div style={getAnswerAreaStyles(highlightIncomplete)}>{renderTextTableFormQuestion(question, questionIndex, response)}</div>}
                {!studentQuestionOnly && !['form', 'table', 'tableForm', 'imageTableForm', 'textTableForm'].includes(question.type) && renderOptionsQuestion(question, questionIndex, response, highlightIncomplete, retrySectionState)}
                {assessorMode && showAssessorMark && !inlineMarkingMode && !rightAlignedMarkingMode && <div style={{ display: 'grid', gap: '10px', gridTemplateColumns: '160px', alignItems: 'start', marginTop: '18px', marginBottom: '16px', padding: '12px', backgroundColor: '#f7f7f7', borderRadius: '8px', maxWidth: '220px' }}>
                    <div>
                        <label className='fieldHeading' style={{ marginTop: 0 }}>Mark</label>
                        {renderFieldInput(
                            'select',
                            localAssessorReview?.questionMarks?.[questionIndex]?.mark || '',
                            (nextValue) => updateAssessorReview(questionIndex, 'mark', nextValue),
                            4,
                            '100%',
                            ['C', 'NYC']
                        )}
                        {trainerReviewSectionOptions.length > 0 && (localAssessorReview?.questionMarks?.[questionIndex]?.mark || '') === 'NYC' && (
                            <>
                                <label className='fieldHeading' style={{ marginTop: '10px', marginBottom: '6px', display: 'block' }}>Review Section</label>
                                <select
                                    value={localAssessorReview?.questionMarks?.[questionIndex]?.reviewSection || getDefaultQuestionReviewSection(question)}
                                    onChange={({ target }) => updateAssessorReview(questionIndex, 'reviewSection', target.value)}
                                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ccc', fontFamily: 'inherit', fontSize: '15px', boxSizing: 'border-box', backgroundColor: '#fff', color: '#111' }}
                                >
                                    {trainerReviewSectionOptions.map((option) => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </>
                        )}
                    </div>
                </div>}
                {assessorMode && question.assessorCommentField && <div style={{ marginTop: '18px', marginBottom: '12px', maxWidth: '1000px' }}>
                    <label className='fieldHeading' style={{ marginTop: 0 }}>Assessor Comments</label>
                    {renderFieldInput(
                        'textarea',
                        trainerCommentValue,
                        (nextValue) => updateAssessorReview(questionIndex, 'comment', nextValue),
                        4,
                        '100%'
                    )}
                </div>}
                {renderAssessorGuide(question)}
                </div>
            </div>
        );
    };

    if (completedAssessment && !assessorMode) {
        return (
            <div className='qld-stage-shell' style={{ textAlign: 'center' }}>
                <h1>{assessmentContentConfig.completionHeading}</h1>
                <p style={{ fontSize: '23px', lineHeight: 1.45, maxWidth: '860px', margin: '0 auto' }}>{assessmentContentConfig.completionMessage}</p>
            </div>
        );
    }

    if (activePrefaceScreens.length > 0 && prefaceScreenIndex < activePrefaceScreens.length) {
        const screen = activePrefaceScreens[prefaceScreenIndex];
        const groupedRows = usePdfTheme
            ? screen.body.reduce((acc, paragraph) => {
                if (prefaceSectionLabels.has(paragraph)) {
                    acc.push({ label: paragraph, content: [] });
                } else if (acc.length > 0) {
                    acc[acc.length - 1].content.push(paragraph);
                }
                return acc;
            }, [])
            : [];
        const hasTableRows = groupedRows.length > 0;

        return (
            <div className='qld-stage-shell' style={usePdfTheme ? { paddingTop: '72px' } : undefined}>
                <h1 style={{ color: usePdfTheme ? pdfBlue : '#111', marginBottom: '4px', fontSize: usePdfTheme ? '33px' : undefined, fontWeight: usePdfTheme ? '400' : undefined, lineHeight: usePdfTheme ? 1.05 : undefined }}>{screen.heading}</h1>
                {screen.subheading && <h3 style={{ color: usePdfTheme ? pdfBlue : Grey, marginTop: 0, fontSize: usePdfTheme ? '29px' : undefined, fontWeight: usePdfTheme ? '500' : undefined }}>{screen.subheading}</h3>}
                <div style={{
                    maxWidth: '1080px',
                    border: usePdfTheme && !hasTableRows ? 0 : `1px solid ${pdfBorder}`,
                    borderRadius: usePdfTheme ? 0 : '12px',
                    padding: usePdfTheme ? (hasTableRows ? 0 : '0') : '20px',
                    backgroundColor: '#fff'
                }}>
                    {usePdfTheme && hasTableRows ? (
                        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                            <tbody>
                                {groupedRows.map((row) => (
                                    <tr key={row.label}>
                                        <td style={{ width: '30%', padding: '14px 12px', border: `1px solid ${pdfBorder}`, fontWeight: '700', verticalAlign: 'top' }}>{row.label}</td>
                                        <td style={{ padding: '14px 16px', border: `1px solid ${pdfBorder}`, verticalAlign: 'top' }}>
                                            {row.content.map((paragraph) => (
                                                <div key={paragraph} style={{ marginTop: 0, marginBottom: '14px', fontSize: '19px', lineHeight: 1.45 }}>{renderHtmlContent(paragraph)}</div>
                                            ))}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <div style={{ padding: usePdfTheme ? '8px 0' : 0 }}>
                            {screen.body.map((paragraph) => {
                                if (usePdfTheme && prefaceBlueHeadingLabels.has(paragraph)) {
                                    return (
                                        <h2 key={paragraph} style={{ color: pdfBlue, fontSize: '43px', fontWeight: '400', lineHeight: 1.1, margin: '28px 0 16px' }}>
                                            {paragraph}
                                        </h2>
                                    );
                                }

                                return (
                                    <div
                                        key={paragraph}
                                        style={{ marginTop: 0, marginBottom: '18px', fontSize: usePdfTheme ? '19px' : undefined, lineHeight: usePdfTheme ? 1.45 : undefined }}
                                    >
                                        {renderHtmlContent(paragraph)}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
                <div style={{ marginTop: '20px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    <button className="buttonPrimary" style={primaryButtonStyles} onClick={() => setPrefaceScreenIndex((previous) => previous + 1)}>
                        {screen.buttonLabel || 'Next'}
                    </button>
                </div>
            </div>
        );
    }

    if (questions.length === 0 || currentQuestions.length === 0) {
        return <></>;
    }

    if (showIncorrectReviewScreen && !assessorMode) {
        return (
            <div className='qld-stage-shell' style={{ textAlign: 'center' }}>
                <h1>Almost there!</h1>
                <h3 style={{ color: Grey, marginTop: 0, fontSize: '25px', lineHeight: 1.4 }}>Please review your incorrect answers.</h3>
                <div style={{ marginTop: '20px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    <button className="buttonPrimary" style={{ ...primaryButtonStyles, margin: '0 auto' }} onClick={() => startIncorrectReview()}>
                        Next
                    </button>
                </div>
            </div>
        );
    }

    if (showRetryIntroScreen && !assessorMode) {
        return (
            <div className='qld-stage-shell'>
                <h1>{assessmentContentConfig.retryIntroHeading}</h1>
                <h3 style={{ color: Grey, marginTop: 0, fontSize: '25px', lineHeight: 1.4 }}>Please review your incorrect answers.</h3>
                <div style={{ marginTop: '20px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    <button className="buttonPrimary" style={primaryButtonStyles} onClick={() => setShowRetryIntroScreen(false)}>
                        Begin
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className='qld-stage-shell'>
            <div style={usePdfTheme ? { borderTop: '2px solid #c9d9ee', paddingTop: '18px' } : undefined}>
            <h1 style={{ color: usePdfTheme ? pdfBlue : '#111', marginBottom: '4px', fontSize: usePdfTheme ? '33px' : undefined, fontWeight: usePdfTheme ? '400' : undefined, lineHeight: usePdfTheme ? 1.05 : undefined }}>{studentRetryMode ? assessmentContentConfig.retryHeading : assessmentContentConfig.title}</h1>
            {assessmentContentConfig.subtitle && <h3 style={{ color: usePdfTheme ? pdfBlue : Grey, marginTop: 0, fontWeight: usePdfTheme ? '500' : undefined, fontSize: usePdfTheme ? '29px' : '25px', lineHeight: 1.35 }}>{studentRetryMode ? assessmentContentConfig.retrySubtitle : assessmentContentConfig.subtitle}</h3>}
            {assessorMode && showMarkAllAsC && !embeddedMarkAllCMode && !rightAlignedMarkingMode && !inlineMarkingMode && (
                <div style={{ marginBottom: '18px', display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                        type='button'
                        onClick={markAllQuestionsCompetent}
                        style={{
                            border: `1px solid ${Green}`,
                            background: '#eaf7ed',
                            color: Green,
                            fontWeight: '700',
                            padding: '10px 14px',
                            borderRadius: '8px',
                            cursor: 'pointer',
                        }}
                    >
                        Mark All as C
                    </button>
                </div>
            )}
            {assessorMode && trainerIncorrectQuestionIndexes.length > 0 && <div style={{ marginBottom: '16px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <button
                    type='button'
                    onClick={() => {
                        setTrainerViewMode('all');
                        setCurrentPageIndex(0);
                    }}
                    style={trainerViewMode === 'all' ? primaryButtonStyles : secondaryButtonStyles}
                >
                    View All
                </button>
                <button
                    type='button'
                    onClick={() => {
                        setTrainerViewMode('incorrect');
                        setCurrentPageIndex(0);
                    }}
                    style={trainerViewMode === 'incorrect' ? primaryButtonStyles : secondaryButtonStyles}
                >
                    View Only Marked Incorrect
                </button>
            </div>}
            {currentQuestions.map((question, offset) => renderQuestion(question, currentQuestionIndexes[offset]))}
            {pageValidationMessage && <p style={{ color: '#c62828', fontWeight: '600', marginTop: '8px' }}>{pageValidationMessage}</p>}
            <div style={{ marginTop: '20px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <button type='button' className="buttonSecondary" style={secondaryButtonStyles} onClick={handlePrev} disabled={currentPageIndex === 0}>Previous Page</button>
                <span style={{ alignSelf: 'center', color: '#667085', fontWeight: '600' }}>Page {currentPageIndex + 1} of {groupedQuestionIndexes.length}</span>
                <button type='button' className="buttonPrimary" style={primaryButtonStyles} onClick={handleNext}>{isLastPage ? assessmentContentConfig.nextLabel : 'Next Page'}</button>
            </div>
            </div>
        </div>
    );
};

export default QldAssessmentFlow;
